import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { api } from '../../lib/apiClient.js';
import { useApi, useMutation } from '../../lib/useApi.js';
import { humanizeEnum, sourceSystemLabel } from '../../lib/fieldLabels.js';
import { ProvenanceBadge, normalizedProvenance } from '../../components/ProvenanceBadge.jsx';
import {
  appendWorkspaceReturnContext,
  buildWorkspaceReturnTarget,
} from '../../lib/workspaceContext.js';
import {
  ConfidenceBadge,
  DataSourceBadge,
  EmptyState,
  ErrorState,
  FormField,
  LoadingSkeleton,
  MetricCard,
  PageActionStrip,
  PageHeader,
  Select,
  StatusChip,
  Textarea,
  TextInput,
  toast,
} from '../../components/index.js';
import {
  buildExecutiveDeckPath,
  buildInformationPaperPath,
} from '../briefings/reportEntryLinks.js';
import './owha.css';

function bucketLabel(bucket) {
  return String(bucket || 'proposed')
    .replace(/^\w/, (letter) => letter.toUpperCase())
    .replace(/_/g, ' ');
}

function formatConfiguredValue(value) {
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Not provided';
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (value == null || value === '') return 'Not provided';
  return String(value);
}

function formatReviewStateLabel(value) {
  const normalized = String(value || 'generated')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return normalized ? normalized.replace(/\b\w/g, (letter) => letter.toUpperCase()) : 'Generated';
}

function formatReviewDispositionLabel(value) {
  const normalized = String(value || 'pending_human_review')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return normalized
    ? normalized.replace(/\b\w/g, (letter) => letter.toUpperCase())
    : 'Pending Human Review';
}

function formatWorkflowStateLabel(value) {
  const normalized = String(value || 'draft')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return normalized ? normalized.replace(/\b\w/g, (letter) => letter.toUpperCase()) : 'Draft';
}

function formatIntakeRequestTypeLabel(value) {
  return String(value || '')
    .toLowerCase()
    .includes('support')
    ? 'Request for Support'
    : 'Project Intake Form (PIF)';
}

function formatTrustUpdatedAt(value) {
  if (!value) return 'n/a';
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) return String(value);
  return timestamp.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatFreshnessLabel(value) {
  const normalized = String(value || 'unknown')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return normalized ? normalized.replace(/\b\w/g, (letter) => letter.toUpperCase()) : 'Unknown';
}

function formatTrendDirectionLabel(value) {
  switch (String(value || '').toLowerCase()) {
    case 'improving':
      return 'Improving trend';
    case 'declining':
      return 'Declining trend';
    default:
      return 'Steady trend';
  }
}

function formatHoursPerWeek(value) {
  return `${Number(value || 0)}h/week`;
}

function formatUtilizationLabel(value) {
  return `${Number(value || 0)}% utilization`;
}

function formatAuditActionLabel(value) {
  const normalized = String(value || '')
    .replace(/[._-]+/g, ' ')
    .trim();
  return normalized ? normalized.replace(/\b\w/g, (letter) => letter.toUpperCase()) : 'Audit event';
}

function formatAuditTimestamp(value) {
  if (!value) return 'Timestamp unavailable';
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) return String(value);
  return timestamp.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function pluralize(value, singular, plural = `${singular}s`) {
  return Number(value || 0) === 1 ? singular : plural;
}

function formatMissingFlags(flags = []) {
  if (!Array.isArray(flags) || flags.length === 0) return 'None';
  // humanizeEnum sentence-cases while preserving acronyms, so
  // `loe_alignment_missing` reads "LOE alignment missing" rather than the
  // title-cased "Loe Alignment Missing".
  return flags.map((flag) => humanizeEnum(flag)).join(', ');
}

function AiProvenanceFacts({ run }) {
  const provenance = normalizedProvenance(run, 'ai');
  const facts = [
    ['Execution type', humanizeEnum(provenance.executionType)],
    ['Requested provider', humanizeEnum(provenance.requestedProvider)],
    ['Actual provider', humanizeEnum(provenance.actualProvider)],
    ['Model invoked', provenance.modelInvoked ? 'Yes' : 'No'],
    ['Model ID', provenance.modelId || 'Not invoked'],
    ['Corpus mode', humanizeEnum(provenance.corpusMode)],
    [
      'Fallback',
      provenance.fallback.occurred ? humanizeEnum(provenance.fallback.reason) : 'Not used',
    ],
    [
      'Partial execution',
      provenance.partialExecution.occurred
        ? humanizeEnum(provenance.partialExecution.reason)
        : 'None',
    ],
    ['Confidence source', humanizeEnum(provenance.confidence.source)],
    ['Citation score source', humanizeEnum(provenance.citationScoreSource)],
    ['Seeded', provenance.seeded ? 'Yes' : 'No'],
    ['Illustrative / demo', provenance.illustrative ? 'Yes' : 'No'],
  ];

  return (
    <section className="owha-ai-provenance" aria-label="AI execution provenance">
      <div className="owha-ai-provenance-head">
        <strong>Execution provenance</strong>
        <ProvenanceBadge value={run} kind="ai" />
      </div>
      <dl className="owha-ai-provenance-grid" data-testid="intake-ai-provenance">
        {facts.map(([label, value]) => (
          <div className="owha-ai-provenance-fact" key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function normalizeIntakeCriteriaRows(criteria = []) {
  return Array.isArray(criteria)
    ? criteria.map((row) => ({
        criterion: row.criterion || row.label || row.key || 'Criterion',
        aiSuggestedScore: Number(row.score ?? row.aiSuggestedScore ?? row.selectedScore ?? 0),
        selectedScore: Number(row.selectedScore ?? row.score ?? row.aiSuggestedScore ?? 0),
        rationale: row.rationale || '',
        confidence: row.confidence ?? null,
        citations: Array.isArray(row.citations) ? row.citations : [],
      }))
    : [];
}

function mergeIntakeAiBaseline(detail, aiPayload) {
  if (!detail) return detail;
  const aiBaselineScore = Number(
    aiPayload?.baselineScore ?? detail?.scoring?.aiBaselineScore ?? detail?.scoring?.total ?? 0,
  );
  return {
    ...detail,
    scoring: {
      ...detail.scoring,
      total: aiBaselineScore,
      aiBaselineScore,
      officialScore: detail.scoring?.officialScore ?? detail.request?.review?.officialScore ?? null,
      reviewerScore: detail.scoring?.reviewerScore ?? detail.request?.review?.reviewerScore ?? null,
      reviewDisposition: detail.scoring?.reviewDisposition || 'pending_human_review',
      rows: normalizeIntakeCriteriaRows(aiPayload?.criteria),
      missingInformationFlags: Array.isArray(aiPayload?.missingInformationFlags)
        ? aiPayload.missingInformationFlags
        : [],
      aiRun: {
        runId: aiPayload?.runId || null,
        reviewState: aiPayload?.reviewState || 'pending_human_review',
        confidence: aiPayload?.confidence ?? null,
        citationCount: Array.isArray(aiPayload?.citations) ? aiPayload.citations.length : 0,
        citations: Array.isArray(aiPayload?.citations) ? aiPayload.citations : [],
        guardrailOutcome: aiPayload?.guardrailOutcome || 'passed',
        provenance: aiPayload?.provenance || null,
        provider: aiPayload?.provider || null,
        mode: aiPayload?.mode || null,
        modelId: aiPayload?.modelId ?? null,
        modelInvoked: aiPayload?.modelInvoked === true,
        confidenceSource: aiPayload?.confidenceSource || null,
        citationScoreSource:
          aiPayload?.citationScoreSource || aiPayload?.provenance?.citationScoreSource || null,
      },
    },
  };
}

function collectConfiguredFields(configuration = {}) {
  return (configuration.sections || []).flatMap((section) => section.fields || []);
}

function buildIntakeFormValues(detail) {
  const request = detail?.request || {};
  return collectConfiguredFields(detail?.configuration).reduce(
    (values, field) => {
      values[field.fieldKey] = request[field.fieldKey] ?? field.value ?? '';
      return values;
    },
    {
      title: request.title || '',
      primaryLoeId: request.primaryLoeId || '',
      problemStatement: request.problemStatement || '',
      requestSummary: request.requestSummary || '',
      missionImpact: request.missionImpact || '',
      urgency: request.urgency || '',
      partnerDirectorate:
        request.customFields?.partnerDirectorate || request.partnerDirectorate || '',
    },
  );
}

function fieldHasValue(value) {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean' || typeof value === 'number') return true;
  return String(value ?? '').trim().length > 0;
}

function collectMissingIntakeFieldEntries(detail, formValues) {
  return collectConfiguredFields(detail?.configuration)
    .filter((field) => field.required)
    .filter((field) => !fieldHasValue(formValues[field.fieldKey]))
    .map((field) => ({ fieldKey: field.fieldKey, label: field.label || field.fieldKey }));
}

function collectMissingIntakeFields(detail, formValues) {
  return collectMissingIntakeFieldEntries(detail, formValues).map((field) => field.label);
}

// Standard intake fields carry their own first-class payload keys above; the
// configured-field loop only owns the additional admin-created custom fields.
const STANDARD_INTAKE_FIELD_KEYS = new Set([
  'title',
  'primaryLoeId',
  'problemStatement',
  'requestSummary',
  'missionImpact',
  'urgency',
  'partnerDirectorate',
]);

export function buildDraftPayload(detail, requestType, formValues) {
  const payload = {
    id: detail?.request?.id || undefined,
    requestType,
    title: formValues.title || '',
    primaryLoeId: formValues.primaryLoeId || '',
    missionImpact: formValues.missionImpact || '',
    urgency: formValues.urgency || '',
  };
  if (requestType === 'request_for_support') {
    payload.requestSummary = formValues.requestSummary || '';
  } else {
    payload.problemStatement = formValues.problemStatement || '';
    payload.partnerDirectorate = formValues.partnerDirectorate || '';
  }

  // Persist ANY configured custom field the form is showing (not a hardcoded
  // allowlist), so admin-created fields survive save. Masking write-guard, client
  // half: never write back a value the viewer only ever saw masked — the server
  // enforces the same invariant so a masked viewer cannot clobber the real value.
  const customFields = {};
  for (const field of collectConfiguredFields(detail?.configuration)) {
    if (STANDARD_INTAKE_FIELD_KEYS.has(field.fieldKey)) continue;
    if (field.masked) continue;
    if (formValues[field.fieldKey] !== undefined) {
      customFields[field.fieldKey] = formValues[field.fieldKey];
    }
  }
  if (Object.keys(customFields).length > 0) {
    payload.customFields = customFields;
  }
  return payload;
}

function buildRedundancyReviewPath({ scopeType, scopeId, returnTo, returnLabel }) {
  const params = new URLSearchParams({
    mode: 'redundancy',
    scopeType,
    scopeId,
  });
  if (returnTo) params.set('returnTo', returnTo);
  if (returnLabel) params.set('returnLabel', returnLabel);
  return `/ai/orchestration?${params.toString()}`;
}

function buildDisplayedAiSummary(baseSummary, draftRun) {
  if (!draftRun) return baseSummary;
  const summary = baseSummary || {
    totalRuns: 0,
    pendingReviewCount: 0,
    reviewedCount: 0,
    rejectedCount: 0,
    guardrailAttentionCount: 0,
  };
  return {
    ...summary,
    totalRuns: (summary.totalRuns || 0) + 1,
    pendingReviewCount: (summary.pendingReviewCount || 0) + 1,
    latestRun: draftRun,
  };
}

const DEFAULT_LOE_FILTERS = {
  divisionId: '',
  portfolioId: '',
  fiscalYear: '',
  lifecycleStage: '',
  projectStatus: '',
  resourceTeamId: '',
  riskSeverity: '',
};

function isStrategicExecutiveOrganizationName(name) {
  return /strategic (executive|execution)/i.test(String(name || ''));
}

function deriveDivisionScopedViewer({ user, roles = [], organizations = [] }) {
  const homeOrganizationId = user?.homeOrganizationId || user?.home_org_id || '';
  const homeOrganization =
    organizations.find(
      (organization) => String(organization.id || '') === String(homeOrganizationId),
    ) || null;
  const isExecutiveViewer = roles.includes('ROLE-EXECUTIVE-SPONSOR');
  const hasDivisionScopedRole =
    roles.includes('ROLE-PORTFOLIO-MANAGER') || roles.includes('ROLE-PROGRAM-MANAGER');
  if (
    !isExecutiveViewer &&
    hasDivisionScopedRole &&
    homeOrganizationId &&
    homeOrganization &&
    !isStrategicExecutiveOrganizationName(homeOrganization.name)
  ) {
    return {
      divisionId: homeOrganizationId,
      divisionName: homeOrganization.shortName || homeOrganization.name || homeOrganizationId,
      actorTitle: user?.title || 'Division lead',
    };
  }
  return null;
}

const PROJECT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'stable', label: 'Stable' },
  { value: 'watch', label: 'Watch' },
  { value: 'atRisk', label: 'At risk' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'completed', label: 'Completed' },
  { value: 'closed', label: 'Closed' },
];

const RISK_SEVERITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium / Moderate' },
  { value: 'low', label: 'Low' },
];

const LIFECYCLE_STAGE_OPTIONS = [
  { value: 'intake', label: 'Intake' },
  { value: 'approval', label: 'Approval' },
  { value: 'diagnose', label: 'Diagnose' },
  { value: 'implement', label: 'Implement' },
  { value: 'execution', label: 'Execution' },
  { value: 'closeout', label: 'Closeout' },
  { value: 'sustainment', label: 'Sustainment' },
];

const LOE_DRILLDOWN_BUCKETS = [
  { key: 'proposed', label: 'Proposed' },
  { key: 'current', label: 'Active' },
  { key: 'completed', label: 'Completed' },
  { key: 'denied', label: 'Denied' },
  { key: 'deferred', label: 'Deferred' },
  { key: 'archived', label: 'Archived' },
];

function formatLoeBucketRollupLabel(bucketKey) {
  return String(bucketKey || '') === 'current' ? 'Current' : bucketLabel(bucketKey);
}

function normalizeLoeBucketFilter(value) {
  const normalized = String(value || '').toLowerCase();
  return LOE_DRILLDOWN_BUCKETS.some((bucket) => bucket.key === normalized) ? normalized : null;
}

const LOE_WORKSPACE_TABS = [
  { key: 'prioritize', label: 'Prioritize' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'resources', label: 'Resources & Budget' },
  { key: 'evidence', label: 'Evidence & AI' },
];

function normalizeLoeWorkspaceTab(value) {
  const normalized = String(value || '').toLowerCase();
  return LOE_WORKSPACE_TABS.some((tab) => tab.key === normalized) ? normalized : 'prioritize';
}

function buildLoeWorkspacePath(loeId, tab, extraParams = {}) {
  const params = new URLSearchParams({ tab: normalizeLoeWorkspaceTab(tab), ...extraParams });
  return `/analytics/loes/${encodeURIComponent(loeId)}?${params.toString()}`;
}

function buildLoeBucketFilterPath(loeId, bucketKey) {
  return buildLoeWorkspacePath(loeId, 'delivery', { bucket: bucketKey });
}

// AC-TRUST-3: every citation navigates to the cited record — artifact dossiers for
// knowledge artifacts, the owning record route for intake/project/risk citations.
function buildCitationRoute(citation = {}) {
  const token = String(citation.artifactId || citation.citationId || citation.source || '').trim();
  if (!token) return '/knowledge';
  if (/^PRJ-/i.test(token)) return `/projects/${encodeURIComponent(token)}`;
  if (/^REQ-/i.test(token)) return `/project-factory/requests/${encodeURIComponent(token)}`;
  if (/^RSK-/i.test(token)) return '/governance/risks';
  return `/knowledge/artifacts/${encodeURIComponent(token)}`;
}

const INTAKE_DECISION_META = {
  approved: {
    actionLabel: 'Approve for project factory',
    busyLabel: 'Approving...',
    confirmTitle: 'Approve this intake?',
    confirmLabel: 'Confirm approval',
    consequence:
      'Approves this request into the project factory. Your rationale is recorded on the intake decision history with your name and timestamp.',
  },
  deferred: {
    actionLabel: 'Defer',
    busyLabel: 'Deferring...',
    confirmTitle: 'Defer this intake?',
    confirmLabel: 'Confirm deferral',
    consequence:
      'Holds this request for follow-up instead of advancing it. Your rationale is recorded on the intake decision history with your name and timestamp.',
  },
  denied: {
    actionLabel: 'Deny',
    busyLabel: 'Denying...',
    confirmTitle: 'Deny this intake?',
    confirmLabel: 'Confirm denial',
    consequence:
      'Denies this request. This is an outward-facing decision the requester will see. Your rationale is recorded on the intake decision history with your name and timestamp.',
  },
  request_more_information: {
    actionLabel: 'Request more info',
    busyLabel: 'Sending request...',
    confirmTitle: 'Request more information?',
    confirmLabel: 'Confirm request for more information',
    consequence:
      'Returns this request to the intake owner for clarification. Your rationale is recorded on the intake decision history with your name and timestamp.',
  },
};

// Shared decision-confirm dialog (AC-INT-3.5, AC-LOE-2.2, AC-AUD-2): every decision
// requires an explicitly typed/confirmed rationale — never a canned audit string.
function DecisionRationaleDialog({
  idPrefix,
  title,
  consequence,
  confirmLabel,
  rationale,
  onRationaleChange,
  onCancel,
  onConfirm,
  busy,
  error,
}) {
  const trimmedRationale = String(rationale || '').trim();
  return (
    <div className="owha-confirm-veil" role="presentation">
      <div
        className="card owha-confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${idPrefix}-title`}
      >
        <h3 id={`${idPrefix}-title`}>{title}</h3>
        <p className="muted">{consequence}</p>
        <FormField
          label="Decision rationale"
          htmlFor={`${idPrefix}-rationale`}
          required
          error={
            trimmedRationale
              ? undefined
              : 'Rationale is required — it becomes part of the auditable decision record.'
          }
        >
          <Textarea
            id={`${idPrefix}-rationale`}
            rows={4}
            value={rationale}
            onChange={onRationaleChange}
            placeholder="Why this decision, why now..."
          />
        </FormField>
        {error ? (
          <p className="form-error" role="alert">
            {error.message || 'The decision could not be recorded.'}
          </p>
        ) : null}
        <div className="owha-review-actions">
          <button className="btn ghost sm" type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn primary sm"
            type="button"
            onClick={onConfirm}
            disabled={busy || !trimmedRationale}
          >
            {busy ? 'Recording decision...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const LOE_PRIORITY_SORT_OPTIONS = [
  { value: 'ai_score', label: 'AI score' },
  { value: 'mission_impact', label: 'Mission impact' },
  { value: 'priority_rank', label: 'Priority rank' },
];

function priorityMetricValue(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : -1;
}

function sortPriorityCandidates(rows = [], sortBy = 'ai_score') {
  const items = Array.isArray(rows) ? [...rows] : [];
  return items.sort((left, right) => {
    const aiDelta = priorityMetricValue(right.aiScore) - priorityMetricValue(left.aiScore);
    const missionDelta =
      priorityMetricValue(right.missionImpactScore) - priorityMetricValue(left.missionImpactScore);
    const rankDelta =
      priorityMetricValue(left.priorityRank) - priorityMetricValue(right.priorityRank);

    if (sortBy === 'mission_impact') {
      return (
        missionDelta ||
        aiDelta ||
        rankDelta ||
        String(left.title || '').localeCompare(String(right.title || ''))
      );
    }
    if (sortBy === 'priority_rank') {
      return (
        rankDelta ||
        aiDelta ||
        missionDelta ||
        String(left.title || '').localeCompare(String(right.title || ''))
      );
    }
    return (
      aiDelta ||
      missionDelta ||
      rankDelta ||
      String(left.title || '').localeCompare(String(right.title || ''))
    );
  });
}

function moveGroupedRecordToBucket(groupedRecords, recordId, nextBucket, replacementRecord) {
  const nextGrouped = {
    counts: {},
  };

  for (const bucket of LOE_DRILLDOWN_BUCKETS) {
    nextGrouped[bucket.key] = (groupedRecords?.[bucket.key] || []).filter(
      (row) => String(row.id || '') !== recordId,
    );
  }

  if (nextBucket && nextGrouped[nextBucket]) {
    nextGrouped[nextBucket] = [replacementRecord, ...nextGrouped[nextBucket]];
  }

  for (const bucket of LOE_DRILLDOWN_BUCKETS) {
    nextGrouped.counts[bucket.key] = nextGrouped[bucket.key].length;
  }

  return nextGrouped;
}

function buildLoeQuery(filters) {
  return Object.entries(filters).reduce(
    (query, [key, value]) => {
      if (value) query[key] = value;
      return query;
    },
    { limit: 100 },
  );
}

const queueStatusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'scored', label: 'Scored' },
  { value: 'clarification_needed', label: 'Clarification needed' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
  { value: 'deferred', label: 'Deferred' },
  { value: 'generated', label: 'Generated' },
];

const queueRequestTypeOptions = [
  { value: '', label: 'All request types' },
  { value: 'project_intake', label: 'Project Intake' },
  { value: 'request_for_support', label: 'Request for Support' },
];

const queueSortOptions = [
  { value: 'priority_asc', label: 'Priority (highest first)' },
  { value: 'priority_desc', label: 'Priority (lowest first)' },
  { value: 'title_asc', label: 'Title (A-Z)' },
  { value: 'title_desc', label: 'Title (Z-A)' },
];

function normalizeQueueToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

function normalizeQueueStatusToken(value) {
  return normalizeQueueToken(value).replace(/[\s_-]+/g, '');
}

function buildQueueRowSearchToken(row = {}) {
  return [
    row.id,
    row.title,
    row.loe,
    row.workflowState,
    row.statusKey,
    row.reviewerName,
    row.requestTypeKey,
    row.requestType,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function sortQueueRows(rows, sortBy) {
  const [field, direction = 'asc'] = String(sortBy || 'priority_asc').split('_');
  const sortDirection = direction === 'desc' ? -1 : 1;
  const getValue = (row) => {
    if (field === 'title') return row.title || '';
    return Number(row.priorityRank || 999);
  };
  return [...rows].sort((left, right) => {
    const leftValue = getValue(left);
    const rightValue = getValue(right);
    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      if (leftValue !== rightValue) return (leftValue - rightValue) * sortDirection;
    } else {
      const comparison = String(leftValue || '').localeCompare(String(rightValue || ''));
      if (comparison !== 0) return comparison * sortDirection;
    }
    return String(left.id || '').localeCompare(String(right.id || ''));
  });
}

export function ProjectFactoryPage() {
  const res = useApi('/project-factory');
  const rows = res.data?.data || [];
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loe, setLoe] = useState('');
  const [reviewer, setReviewer] = useState('');
  const [requestType, setRequestType] = useState('');
  const [sortBy, setSortBy] = useState('priority_asc');

  usePageMetadata({
    title: 'Intake Queue',
    subtitle: 'Review PIFs, route requests, and generate projects aligned to Director LOEs.',
  });

  if (res.loading) return <LoadingSkeleton rows={6} />;
  if (res.error) return <ErrorState error={res.error} onRetry={res.refresh} />;
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No intake requests"
        description="Seed or import an OWHA intake queue to begin."
      />
    );
  }

  const bucketCounts = rows.reduce(
    (acc, row) => {
      acc[row.decisionBucket] = (acc[row.decisionBucket] || 0) + 1;
      return acc;
    },
    { proposed: 0, current: 0, completed: 0, denied: 0, deferred: 0 },
  );
  const loeOptions = [
    { value: '', label: 'All LOEs' },
    ...[...new Set(rows.map((row) => row.loe).filter(Boolean))]
      .sort((left, right) => String(left).localeCompare(String(right)))
      .map((value) => ({ value, label: value })),
  ];
  const reviewerOptions = [
    { value: '', label: 'All reviewers' },
    ...[...new Set(rows.map((row) => row.reviewerName).filter(Boolean))]
      .sort((left, right) => String(left).localeCompare(String(right)))
      .map((value) => ({ value, label: value })),
  ];
  const filteredRows = sortQueueRows(
    rows.filter((row) => {
      if (
        status &&
        normalizeQueueStatusToken(row.statusKey) !== normalizeQueueStatusToken(status)
      ) {
        return false;
      }
      if (loe && normalizeQueueToken(row.loe) !== normalizeQueueToken(loe)) return false;
      if (reviewer && normalizeQueueToken(row.reviewerName) !== normalizeQueueToken(reviewer)) {
        return false;
      }
      if (
        requestType &&
        normalizeQueueStatusToken(row.requestTypeKey || row.requestType) !==
          normalizeQueueStatusToken(requestType)
      ) {
        return false;
      }
      if (search && !buildQueueRowSearchToken(row).includes(normalizeQueueToken(search))) {
        return false;
      }
      return true;
    }),
    sortBy,
  );

  return (
    <section className="owha-grid" data-testid="owha-project-factory-page">
      <PageHeader
        eyebrow="OWHA Lifecycle"
        title="Intake Queue"
        subtitle="Mission-first intake review, LOE alignment, and project generation without leaving the PRIME operating shell."
        actions={
          <div className="row">
            <Link className="btn ghost" to="/loe-dashboard">
              Open LOE dashboard
            </Link>
            <Link className="btn primary" to="/project-factory/requests/new">
              New intake request
            </Link>
          </div>
        }
      />

      <div className="owha-summary-grid">
        <MetricCard
          label="Proposed"
          value={bucketCounts.proposed}
          footer="Requests awaiting decision"
        />
        <MetricCard
          label="Current"
          value={bucketCounts.current}
          footer="Approved items in active delivery"
        />
        <MetricCard
          label="Deferred"
          value={bucketCounts.deferred}
          footer="Routed or held for follow-up"
        />
        <MetricCard
          label="Completed"
          value={bucketCounts.completed}
          footer="Requests with delivered project outcomes"
        />
      </div>

      <div className="card">
        <div className="owha-filter-strip">
          <FormField label="Search queue" htmlFor="owha-queue-search">
            <TextInput
              id="owha-queue-search"
              value={search}
              onChange={setSearch}
              placeholder="Search title, LOE, reviewer, or request ID"
            />
          </FormField>
          <FormField label="Status" htmlFor="owha-queue-status">
            <Select
              id="owha-queue-status"
              value={status}
              onChange={setStatus}
              options={queueStatusOptions}
            />
          </FormField>
          <FormField label="LOE" htmlFor="owha-queue-loe">
            <Select id="owha-queue-loe" value={loe} onChange={setLoe} options={loeOptions} />
          </FormField>
          <FormField label="Reviewer" htmlFor="owha-queue-reviewer">
            <Select
              id="owha-queue-reviewer"
              value={reviewer}
              onChange={setReviewer}
              options={reviewerOptions}
            />
          </FormField>
          <FormField label="Request type" htmlFor="owha-queue-request-type">
            <Select
              id="owha-queue-request-type"
              value={requestType}
              onChange={setRequestType}
              options={queueRequestTypeOptions}
            />
          </FormField>
          <FormField label="Sort by" htmlFor="owha-queue-sort">
            <Select
              id="owha-queue-sort"
              value={sortBy}
              onChange={setSortBy}
              options={queueSortOptions}
            />
          </FormField>
        </div>
      </div>

      {filteredRows.length === 0 ? (
        <EmptyState
          title="No intake requests match these filters"
          description="Adjust the queue filters to bring draft, submitted, scored, approved, denied, deferred, or generated work back into view."
        />
      ) : (
        <div className="owha-queue-grid">
          {filteredRows.map((row) => (
            <article className="card owha-request-card" key={row.id}>
              <div className="owha-review-head">
                <div>
                  <div className="eyebrow">{row.loe}</div>
                  <h2 className="card-title">{row.title}</h2>
                </div>
                <StatusChip status={row.decisionBucket}>
                  {bucketLabel(row.decisionBucket)}
                </StatusChip>
              </div>
              <div className="row">
                <span className="muted small">{row.id}</span>
                {row.reviewerScore != null ? (
                  <ConfidenceBadge value={Math.min(1, row.reviewerScore / 100)} />
                ) : null}
              </div>
              <p className="muted">
                Workflow {formatWorkflowStateLabel(row.workflowState)} · {row.requestType} · Score{' '}
                {row.scoreTotal}
                {row.reviewerName ? ` · Reviewer ${row.reviewerName}` : ''}
                {row.reviewerScore != null ? ` (${row.reviewerScore})` : ''}
              </p>
              <div className="row">
                <Link className="btn ghost sm" to={`/project-factory/requests/${row.id}`}>
                  Review intake
                </Link>
                {row.linkedProjectId ? (
                  <Link className="btn ghost sm" to={`/projects/${row.linkedProjectId}`}>
                    View project
                  </Link>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export function ProjectFactoryCreatePage() {
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const defaultRequestType = 'pif';
  const [requestType, setRequestType] = useState(defaultRequestType);
  const templateRes = useApi('/intake/new', { query: { requestType } });
  const mutation = useMutation();
  const [detail, setDetail] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [validationMessage, setValidationMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [dirty, setDirty] = useState(false);

  usePageMetadata({
    title: 'New OWHA Intake',
    subtitle:
      'Capture a Project Intake Form or Request for Support, save a governed draft, and submit it into the OWHA review queue.',
  });

  useEffect(() => {
    const nextDetail = detail || templateRes.data?.data || null;
    if (!nextDetail) return;
    // Rebuild values from the (re-fetched) template. Switching REQUEST TYPE
    // re-fetches /intake/new?requestType=… and reloads the matching form; without
    // this merge the requester's already-entered values were wiped. Preserve any
    // value the requester already typed for a field key the new form also has
    // (overlap) — a server-provided value always wins, so a saved draft's real
    // values are never overwritten by a stale local entry.
    setFormValues((previous) => {
      const base = buildIntakeFormValues(nextDetail);
      for (const key of Object.keys(base)) {
        if (!fieldHasValue(base[key]) && fieldHasValue(previous?.[key])) {
          base[key] = previous[key];
        }
      }
      return base;
    });
    setDirty(false);
  }, [detail, templateRes.data]);

  // AC-INT-2.4: warn before the browser discards a dirty intake form.
  useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [dirty]);

  if (templateRes.loading && !detail) return <LoadingSkeleton rows={6} />;
  if (templateRes.error && !detail)
    return <ErrorState error={templateRes.error} onRetry={templateRes.refresh} />;

  const activeDetail = detail || templateRes.data?.data || null;
  if (!activeDetail) {
    return (
      <EmptyState
        title="Intake template unavailable"
        description="Activate an OWHA intake form before creating a new request."
      />
    );
  }

  const configuredSections = activeDetail.configuration?.sections || [];
  const requester = activeDetail.requester || {
    id: currentUser?.user?.id || 'Unknown',
    displayName: currentUser?.user?.id || 'Unknown requester',
    title: currentUser?.user?.title || null,
    organization: currentUser?.user?.homeOrganizationId
      ? { id: currentUser.user.homeOrganizationId, name: currentUser.user.homeOrganizationId }
      : null,
  };
  const missingFields = collectMissingIntakeFields(activeDetail, formValues);
  const requestTypeOptions = activeDetail.requestTypeOptions || [
    { value: 'pif', label: 'Project Intake Form (PIF)' },
    { value: 'request_for_support', label: 'Request for Support' },
  ];

  function guardDirtyNavigation(event) {
    if (
      dirty &&
      !window.confirm('You have unsaved intake changes. Leave this page without saving?')
    ) {
      event.preventDefault();
    }
  }

  function updateFieldValue(fieldKey, value) {
    setValidationMessage('');
    setStatusMessage('');
    setDirty(true);
    setFieldErrors((current) => {
      if (!current[fieldKey]) return current;
      const next = { ...current };
      delete next[fieldKey];
      return next;
    });
    setFormValues((current) => ({ ...current, [fieldKey]: value }));
  }

  async function saveDraft() {
    const response = await mutation.run(() =>
      api.post('/intake', buildDraftPayload(activeDetail, requestType, formValues)),
    );
    const payload = response?.data || response;
    setDetail(payload);
    setRequestType(payload?.request?.requestType || requestType);
    setStatusMessage(`Draft saved as ${payload?.request?.id || 'new intake record'}.`);
    setDirty(false);
    return payload;
  }

  async function submitIntake() {
    const missingEntries = collectMissingIntakeFieldEntries(activeDetail, formValues);
    if (missingEntries.length > 0) {
      setFieldErrors(
        Object.fromEntries(
          missingEntries.map((field) => [field.fieldKey, `${field.label} is required.`]),
        ),
      );
      setValidationMessage(
        `Complete required fields before submit: ${missingEntries
          .map((field) => field.label)
          .join(', ')}`,
      );
      return;
    }
    const savedDetail = activeDetail.request?.id ? activeDetail : await saveDraft();
    const response = await mutation.run(() =>
      api.post(`/intake/${savedDetail.request.id}/submit`, {}),
    );
    const payload = response?.data || response;
    setDetail(payload);
    setStatusMessage('Intake submitted for review.');
    setValidationMessage('');
    setFieldErrors({});
    setDirty(false);
    const submittedId = payload?.request?.id || savedDetail.request?.id;
    if (submittedId) {
      // AC-INT-2.3: hand off to the request's review page with a "what happens next" banner.
      navigate(`/project-factory/requests/${encodeURIComponent(submittedId)}?submitted=1`);
    }
  }

  return (
    <section className="owha-grid" data-testid="owha-project-factory-create-page">
      <PageHeader
        eyebrow="OWHA Intake"
        title="New OWHA intake"
        subtitle="Start with a governed PIF or Request for Support, then save a draft or submit it into the Project Factory queue."
        actions={
          <div className="row">
            <Link className="btn ghost" to="/project-factory" onClick={guardDirtyNavigation}>
              Back to queue
            </Link>
            <button
              className="btn ghost"
              type="button"
              disabled={mutation.running}
              onClick={saveDraft}
            >
              Save draft
            </button>
            <button
              className="btn primary"
              type="button"
              disabled={mutation.running}
              onClick={submitIntake}
            >
              {mutation.running ? 'Submitting...' : 'Submit intake'}
            </button>
          </div>
        }
      />

      <div className="owha-review-layout">
        <article className="card">
          <div className="owha-review-head">
            <div>
              <div className="eyebrow">Intake template</div>
              <h2 className="card-title">
                {activeDetail.configuration?.activeForm?.name || 'OWHA intake form'}
              </h2>
            </div>
            <StatusChip status="draft">
              {formatWorkflowStateLabel(activeDetail.request.workflowState)}
            </StatusChip>
          </div>
          <p className="muted">
            Workflow state: {formatWorkflowStateLabel(activeDetail.request.workflowState)} · Routing
            outcome: {activeDetail.request.routingOutcome}
          </p>
          <div className="owha-review-form-grid">
            <FormField label="Request type" htmlFor="owha-request-type">
              <Select
                id="owha-request-type"
                value={requestType}
                disabled={mutation.running || Boolean(activeDetail.request?.id)}
                onChange={(value) => {
                  setDetail(null);
                  setRequestType(value);
                }}
                options={requestTypeOptions}
              />
            </FormField>
          </div>

          <div className="owha-grid" style={{ gap: 12 }}>
            {configuredSections.map((section) => (
              <section key={section.id || section.label} className="card" style={{ padding: 16 }}>
                <div className="eyebrow">{section.label || 'Section'}</div>
                <div className="owha-review-form-grid">
                  {section.fields.map((field) => {
                    const value = formValues[field.fieldKey] ?? '';
                    const fieldType =
                      field.fieldType || (field.fieldKey === 'primaryLoeId' ? 'select' : 'text');
                    return (
                      <FormField
                        key={field.fieldKey}
                        label={field.label || field.fieldKey}
                        htmlFor={`owha-new-intake-${field.fieldKey}`}
                        required={Boolean(field.required)}
                        error={fieldErrors[field.fieldKey]}
                      >
                        {fieldType === 'select' ? (
                          <Select
                            id={`owha-new-intake-${field.fieldKey}`}
                            value={value}
                            placeholder={field.placeholder || 'Select an option'}
                            onChange={(nextValue) => updateFieldValue(field.fieldKey, nextValue)}
                            options={field.options || []}
                          />
                        ) : fieldType === 'textarea' ? (
                          <Textarea
                            id={`owha-new-intake-${field.fieldKey}`}
                            rows={4}
                            value={value}
                            placeholder={field.placeholder}
                            onChange={(nextValue) => updateFieldValue(field.fieldKey, nextValue)}
                          />
                        ) : (
                          <TextInput
                            id={`owha-new-intake-${field.fieldKey}`}
                            value={value}
                            placeholder={field.placeholder}
                            onChange={(nextValue) => updateFieldValue(field.fieldKey, nextValue)}
                          />
                        )}
                        {field.masked && field.maskReason ? (
                          <div className="owha-configured-field-note">{field.maskReason}</div>
                        ) : null}
                      </FormField>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>

          {validationMessage ? (
            <p className="form-error" role="alert">
              {validationMessage}
            </p>
          ) : null}
          {mutation.error ? (
            <p className="form-error" role="alert">
              {mutation.error.message}
            </p>
          ) : null}
          {statusMessage ? <p className="owha-form-success">{statusMessage}</p> : null}
        </article>

        <aside className="owha-grid">
          <article className="card">
            <div className="eyebrow">Requester context</div>
            <h2 className="card-title">{requester.displayName}</h2>
            <p className="muted">{requester.title || 'Portfolio lead'}</p>
            <p className="muted">{requester.organization?.name || 'Organization not available'}</p>
          </article>

          <article className="card">
            <div className="eyebrow">Submission posture</div>
            <h2 className="card-title">{formatIntakeRequestTypeLabel(requestType)}</h2>
            <p className="muted">
              Required fields remaining:{' '}
              {missingFields.length ? missingFields.join(', ') : 'Ready to submit'}
            </p>
            <p className="muted">Saved draft ID: {activeDetail.request?.id || 'Not created yet'}</p>
          </article>
        </aside>
      </div>
    </section>
  );
}

export function ProjectFactoryRequestPage() {
  const { id } = useParams();
  const res = useApi(`/intake/${id}`, { skip: !id });
  const mutation = useMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [detail, setDetail] = useState(null);
  const [reviewerScore, setReviewerScore] = useState('');
  const [reviewRationale, setReviewRationale] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [confirmingDecision, setConfirmingDecision] = useState(null);
  const [decisionRationale, setDecisionRationale] = useState('');
  const [includeStaffingConflictDemo, setIncludeStaffingConflictDemo] = useState(false);
  const [activeGenerationScenarioId, setActiveGenerationScenarioId] = useState(null);

  usePageMetadata({
    title: 'OWHA Intake Review',
    subtitle:
      'AI-assisted scoring, human review, and project-factory routing for OWHA intake records.',
  });

  useEffect(() => {
    const nextDetail = res.data?.data || null;
    setDetail(nextDetail);
    setReviewerScore(
      nextDetail?.scoring?.officialScore != null
        ? String(nextDetail.scoring.officialScore)
        : nextDetail?.scoring?.reviewerScore != null
          ? String(nextDetail.scoring.reviewerScore)
          : '',
    );
    setReviewRationale(
      nextDetail?.request?.decision?.rationale || nextDetail?.request?.review?.rationale || '',
    );
  }, [res.data]);

  if (res.loading) return <LoadingSkeleton rows={7} />;
  if (res.error) return <ErrorState error={res.error} onRetry={res.refresh} />;
  if (!detail) return <EmptyState title="Intake not found" />;

  const request = detail.request;
  const configuredSections = (detail.configuration?.sections || [])
    .map((section) => ({
      ...section,
      fields: (section.fields || []).filter(
        (field) => !['title', 'problemStatement', 'loe', 'primaryLoeId'].includes(field.fieldKey),
      ),
    }))
    .filter((section) => section.fields.length > 0);
  const normalizedReviewerScore = Number.parseInt(reviewerScore, 10);
  const aiBaselineScore = Number(detail.scoring?.aiBaselineScore ?? detail.scoring?.total ?? 0);
  const hasGovernedAiRun = Boolean(detail.scoring?.aiRun?.runId);
  const isOverride =
    hasGovernedAiRun &&
    Number.isFinite(normalizedReviewerScore) &&
    normalizedReviewerScore !== aiBaselineScore;
  const reviewRationaleRequired = isOverride && !reviewRationale.trim();
  const canSubmitReview =
    hasGovernedAiRun &&
    Number.isFinite(normalizedReviewerScore) &&
    normalizedReviewerScore >= 0 &&
    normalizedReviewerScore <= 100 &&
    !reviewRationaleRequired;
  const canGenerateProject =
    !detail.lifecycle.linkedProjectId && request.decision?.decision === 'approved';
  // AC-INT-3.2: approve/defer/deny stay gated until a governed AI baseline run exists;
  // the rationale itself is captured in the explicit confirm step (AC-INT-3.5).
  const canSubmitDecision = hasGovernedAiRun;
  const hasUngovernedScores =
    !hasGovernedAiRun &&
    ((detail.scoring.rows || []).length > 0 ||
      Number(detail.scoring.aiBaselineScore ?? detail.scoring.total ?? 0) > 0);
  const showSubmittedBanner = searchParams.get('submitted') === '1';

  async function runTrackedAction(action, fn) {
    setPendingAction(action);
    try {
      return await mutation.run(fn);
    } finally {
      setPendingAction(null);
    }
  }

  async function runAiBaselineScoring() {
    const response = await runTrackedAction('ai_score', () =>
      api.post('/ai/intake-score', {
        requestId: request.id,
      }),
    );
    const payload = response?.data || response;
    setDetail((current) => mergeIntakeAiBaseline(current, payload));
  }

  async function saveHumanReview() {
    const response = await runTrackedAction('save_review', () =>
      api.post(`/intake/${request.id}/score`, {
        reviewerScore: normalizedReviewerScore,
        rationale: reviewRationale,
      }),
    );
    const payload = response?.data || response;
    setDetail(payload);
  }

  function openDecisionConfirm(decision) {
    setDecisionRationale(reviewRationale.trim());
    setConfirmingDecision(decision);
  }

  async function confirmDecision() {
    const decision = confirmingDecision;
    const rationale = decisionRationale.trim();
    if (!decision || !rationale) return;
    try {
      const response = await runTrackedAction(decision, () =>
        api.post(`/intake/${request.id}/decision`, {
          decision,
          rationale,
        }),
      );
      const payload = response?.data || response;
      setDetail(payload);
      setConfirmingDecision(null);
      toast.success(
        `${INTAKE_DECISION_META[decision]?.actionLabel || 'Decision'} recorded with your rationale.`,
      );
    } catch {
      // Keep the confirm dialog open; mutation.error renders inside it.
      toast.error('The intake decision could not be recorded.');
    }
  }

  async function generateProject() {
    const response = await runTrackedAction('generate', () =>
      includeStaffingConflictDemo
        ? api.post(`/project-factory/${request.id}/generate-project`, {
            scenarioId: 'staffing-conflict-demo',
          })
        : api.post(`/project-factory/${request.id}/generate-project`),
    );
    const payload = response?.data || response;
    setActiveGenerationScenarioId(payload?.generationRecord?.scenarioId || null);
    if (payload?.detail) setDetail(payload.detail);
  }

  function dismissSubmittedBanner() {
    const next = new URLSearchParams(searchParams);
    next.delete('submitted');
    setSearchParams(next, { replace: true });
  }

  return (
    <section className="owha-grid" data-testid="owha-project-factory-request-page">
      <PageHeader
        eyebrow={request.loe || 'OWHA Intake'}
        title={request.title}
        subtitle={
          request.problemStatement ||
          'Synthetic OWHA intake record ready for review and generation.'
        }
        actions={
          <div className="row owha-generation-actions">
            <Link className="btn ghost" to="/project-factory">
              Back to queue
            </Link>
            {canGenerateProject ? (
              <label
                className="chip outline sm owha-generation-toggle"
                htmlFor="project-generation-staffing-demo"
              >
                <input
                  id="project-generation-staffing-demo"
                  type="checkbox"
                  checked={includeStaffingConflictDemo}
                  onChange={(event) => setIncludeStaffingConflictDemo(event.target.checked)}
                />
                Include staffing conflict demo
              </label>
            ) : null}
            <button
              className="btn primary"
              type="button"
              onClick={generateProject}
              disabled={mutation.running || !canGenerateProject}
            >
              {pendingAction === 'generate' ? 'Generating project...' : 'Generate project'}
            </button>
          </div>
        }
      />

      {includeStaffingConflictDemo || activeGenerationScenarioId === 'staffing-conflict-demo' ? (
        <p className="owha-scenario-indicator-row">
          <span
            className="chip outline sm owha-scenario-indicator"
            data-testid="project-generation-scenario-indicator"
          >
            Demo scenario: staffing conflict
          </span>
        </p>
      ) : null}

      {showSubmittedBanner ? (
        <article className="card owha-next-steps-banner" data-testid="intake-what-happens-next">
          <div className="owha-review-head">
            <div>
              <div className="eyebrow">Submitted — what happens next</div>
              <h2 className="card-title">Your request is in the OWHA review queue</h2>
            </div>
            <button className="btn ghost sm" type="button" onClick={dismissSubmittedBanner}>
              Dismiss
            </button>
          </div>
          <p className="muted">
            A reviewer runs governed AI baseline scoring, records the official human review, and
            then captures an approve / defer / deny decision with rationale. Every step lands on
            this page&apos;s decision and audit history, so you can follow the request here.
          </p>
        </article>
      ) : null}

      <div className="owha-review-layout">
        <article
          className="card"
          data-testid="feedback-target-intake-ai-review-card"
          data-feedback-id="intake-ai-review-card"
          data-entity-type="intake_request"
          data-entity-id={request.id}
          aria-label="AI baseline plus human governance"
        >
          <div className="owha-review-head">
            <div>
              <div className="eyebrow">Scoring and review</div>
              <h2 className="card-title">AI baseline plus human governance</h2>
            </div>
            <StatusChip status={detail.lifecycle.decisionBucket}>
              {bucketLabel(detail.lifecycle.decisionBucket)}
            </StatusChip>
          </div>
          <p className="muted">
            Workflow state: {formatWorkflowStateLabel(request.workflowState)} · Routing outcome:{' '}
            {request.routingOutcome}
          </p>
          <p className="muted">Request type: {formatIntakeRequestTypeLabel(request.requestType)}</p>
          <div className="owha-summary-grid">
            <MetricCard
              label="AI baseline score"
              value={detail.scoring.aiBaselineScore ?? 'Not run'}
              footer={
                detail.scoring.aiRun?.runId
                  ? `AI run ${detail.scoring.aiRun.runId}`
                  : 'Run governed AI scoring'
              }
            />
            <MetricCard
              label="Official reviewed score"
              value={detail.scoring.officialScore ?? 'Not yet set'}
              footer={formatReviewDispositionLabel(detail.scoring.reviewDisposition)}
            />
          </div>
          <div className="owha-review-head">
            <div>
              <div className="eyebrow">Governed AI baseline</div>
              <h3 className="card-title">Run metadata and review posture</h3>
            </div>
            <button
              className="btn ghost sm"
              type="button"
              disabled={mutation.running}
              onClick={runAiBaselineScoring}
            >
              {pendingAction === 'ai_score' ? 'Running AI scoring...' : 'Run AI baseline scoring'}
            </button>
          </div>
          {hasGovernedAiRun ? (
            <div data-testid="intake-ai-run-metadata">
              <p className="muted">
                AI run ID: {detail.scoring.aiRun.runId} ·{' '}
                {formatReviewStateLabel(detail.scoring.aiRun.reviewState)}
              </p>
              <p className="muted">
                Missing information flags:{' '}
                {formatMissingFlags(detail.scoring.missingInformationFlags)}
              </p>
              <p className="muted">
                {detail.scoring.aiRun.citationCount || detail.scoring.aiRun.citations?.length || 0}{' '}
                citations attached · Confidence{' '}
                {detail.scoring.aiRun.confidence != null
                  ? Number(detail.scoring.aiRun.confidence).toFixed(2)
                  : 'n/a'}{' '}
                · Guardrail {detail.scoring.aiRun.guardrailOutcome || 'passed'}
              </p>
              <AiProvenanceFacts run={detail.scoring.aiRun} />
              {detail.scoring.aiRun.citations?.length ? (
                <div className="owha-citation-strip" data-testid="intake-run-citations">
                  {detail.scoring.aiRun.citations.map((citation, index) => (
                    <Link
                      key={`${citation.source || 'citation'}-${index}`}
                      className="chip outline sm owha-citation-chip"
                      to={buildCitationRoute(citation)}
                      title={citation.excerpt || citation.source}
                    >
                      [{index + 1}] {citation.source || 'Cited evidence'}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div data-testid="intake-ai-run-absent">
              <p className="muted">
                No governed AI run has been recorded for this intake yet. Run AI baseline scoring to
                create one.
              </p>
              {hasUngovernedScores ? (
                <p className="muted" data-testid="intake-ungoverned-score-note">
                  The criterion scores below predate governed AI scoring — treat them as unverified
                  reviewer working notes until a governed run is recorded.
                </p>
              ) : null}
            </div>
          )}
          <table className="owha-score-table">
            <thead>
              <tr>
                <th>Criterion</th>
                <th>AI</th>
                <th>Confidence</th>
                <th>Evidence</th>
              </tr>
            </thead>
            <tbody>
              {detail.scoring.rows.map((row) => (
                <tr key={row.criterion}>
                  <td>
                    <strong>{row.criterion}</strong>
                    {row.rationale ? <div className="muted small">{row.rationale}</div> : null}
                  </td>
                  <td>{row.aiSuggestedScore}</td>
                  <td>{row.confidence != null ? Number(row.confidence).toFixed(2) : 'n/a'}</td>
                  <td>
                    {row.citations?.length ? (
                      <span className="owha-citation-strip">
                        {row.citations.map((citation, index) => (
                          <Link
                            key={`${row.criterion}-citation-${index}`}
                            className="chip outline sm owha-citation-chip"
                            to={buildCitationRoute(citation)}
                            title={citation.excerpt || citation.source}
                            aria-label={`Open citation ${citation.source || index + 1} for ${row.criterion}`}
                          >
                            {citation.source || `Citation ${index + 1}`}
                          </Link>
                        ))}
                      </span>
                    ) : (
                      '0 citations'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {configuredSections.length ? (
            <div style={{ marginTop: 18 }}>
              <div className="eyebrow">Configured intake form</div>
              <h3 className="card-title" style={{ marginBottom: 8 }}>
                {detail.configuration?.activeForm?.name || 'Active intake form'}
              </h3>
              <div className="owha-grid" style={{ gap: 12 }}>
                {configuredSections.map((section) => (
                  <section
                    key={section.id || section.label}
                    className="card"
                    style={{ padding: 16 }}
                  >
                    <div className="eyebrow">{section.label || 'Section'}</div>
                    <ul className="owha-muted-list">
                      {section.fields.map((field) => (
                        <li className="owha-configured-field" key={field.fieldKey}>
                          <div>
                            <strong>{field.label}</strong>:{' '}
                            <span>{formatConfiguredValue(field.value)}</span>
                          </div>
                          {field.masked && field.maskReason ? (
                            <div className="owha-configured-field-note">{field.maskReason}</div>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </div>
          ) : null}

          <div className="owha-review-form">
            <div className="owha-review-head">
              <div>
                <div className="eyebrow">Human review</div>
                <h3 className="card-title">Portfolio governance update</h3>
              </div>
              <StatusChip status={detail.lifecycle.decisionBucket}>
                {bucketLabel(detail.lifecycle.decisionBucket)}
              </StatusChip>
            </div>
            <div className="owha-review-form-grid">
              <FormField label="Reviewer score" htmlFor="owha-reviewer-score">
                <input
                  id="owha-reviewer-score"
                  className="form-input"
                  inputMode="numeric"
                  min="0"
                  max="100"
                  type="number"
                  value={reviewerScore}
                  onChange={(event) => setReviewerScore(event.target.value)}
                />
              </FormField>
              <FormField label="Review rationale" htmlFor="owha-review-rationale">
                <textarea
                  id="owha-review-rationale"
                  className="form-input form-textarea"
                  rows="4"
                  value={reviewRationale}
                  onChange={(event) => setReviewRationale(event.target.value)}
                />
              </FormField>
            </div>
            {reviewRationaleRequired ? (
              <p className="form-error">
                Review rationale is required when overriding the AI baseline score.
              </p>
            ) : null}
            {mutation.error && !confirmingDecision ? (
              <p className="form-error" role="alert">
                {mutation.error.message}
              </p>
            ) : null}
            <div className="owha-review-actions">
              <button
                className="btn ghost sm"
                type="button"
                disabled={mutation.running || !canSubmitReview}
                onClick={saveHumanReview}
              >
                {pendingAction === 'save_review' ? 'Saving review...' : 'Save human review'}
              </button>
              <button
                className="btn secondary sm"
                type="button"
                disabled={mutation.running || !canSubmitDecision}
                onClick={() => openDecisionConfirm('approved')}
              >
                {pendingAction === 'approved'
                  ? INTAKE_DECISION_META.approved.busyLabel
                  : INTAKE_DECISION_META.approved.actionLabel}
              </button>
              <button
                className="btn ghost sm"
                type="button"
                disabled={mutation.running || !canSubmitDecision}
                onClick={() => openDecisionConfirm('deferred')}
              >
                {pendingAction === 'deferred'
                  ? INTAKE_DECISION_META.deferred.busyLabel
                  : INTAKE_DECISION_META.deferred.actionLabel}
              </button>
              <button
                className="btn ghost sm"
                type="button"
                disabled={mutation.running || !canSubmitDecision}
                onClick={() => openDecisionConfirm('denied')}
              >
                {pendingAction === 'denied'
                  ? INTAKE_DECISION_META.denied.busyLabel
                  : INTAKE_DECISION_META.denied.actionLabel}
              </button>
              <button
                className="btn ghost sm"
                type="button"
                disabled={mutation.running}
                onClick={() => openDecisionConfirm('request_more_information')}
              >
                {pendingAction === 'request_more_information'
                  ? INTAKE_DECISION_META.request_more_information.busyLabel
                  : INTAKE_DECISION_META.request_more_information.actionLabel}
              </button>
            </div>
            {!hasGovernedAiRun ? (
              <p className="owha-decision-gate-hint" data-testid="intake-decision-gate-hint">
                Run AI baseline scoring to enable decisions.
              </p>
            ) : null}
          </div>
        </article>

        {confirmingDecision ? (
          <DecisionRationaleDialog
            idPrefix="owha-intake-decision"
            title={INTAKE_DECISION_META[confirmingDecision]?.confirmTitle || 'Confirm decision?'}
            consequence={
              INTAKE_DECISION_META[confirmingDecision]?.consequence ||
              'Your rationale is recorded on the intake decision history with your name and timestamp.'
            }
            confirmLabel={
              INTAKE_DECISION_META[confirmingDecision]?.confirmLabel || 'Confirm decision'
            }
            rationale={decisionRationale}
            onRationaleChange={setDecisionRationale}
            onCancel={() => setConfirmingDecision(null)}
            onConfirm={confirmDecision}
            busy={pendingAction === confirmingDecision}
            error={mutation.error}
          />
        ) : null}

        <aside className="owha-grid">
          <article className="card">
            <div className="eyebrow">LOE alignment</div>
            <h2 className="card-title">{request.loe || 'Unassigned'}</h2>
            <p className="muted">
              Primary LOE ID: {request.primaryLoeId || 'Not explicitly captured'}
            </p>
            <p className="muted">
              Routing outcome: {request.routingOutcome || 'Pending portfolio review'}
            </p>
          </article>

          <article className="card">
            <div className="eyebrow">Lifecycle</div>
            <h2 className="card-title">Generation status</h2>
            <ul className="owha-muted-list">
              <li>
                Linked RAPID:{' '}
                {detail.lifecycle.linkedRapidId ? (
                  <Link
                    data-testid="intake-linked-rapid-link"
                    to={`/rapids/${detail.lifecycle.linkedRapidId}`}
                  >
                    {detail.lifecycle.linkedRapidId}
                  </Link>
                ) : (
                  'Not linked yet'
                )}
              </li>
              <li>
                Linked project: {detail.lifecycle.linkedProjectId || 'Generate from this review'}
              </li>
              <li>Current stage: {detail.lifecycle.currentStage}</li>
              {/* Feature F — surface the portfolio assigned at generation. When
                  the project generated without a resolvable portfolio, show an
                  explicit "needs portfolio assignment" chip instead of a silent
                  blank so the leader can see the gap. */}
              {detail.lifecycle.linkedProjectId ? (
                <li data-testid="generation-portfolio-status">
                  Portfolio:{' '}
                  {detail.lifecycle.portfolioId ? (
                    <>
                      <strong>{detail.lifecycle.portfolioId}</strong>
                      {detail.lifecycle.loeAlignmentSource === 'loe_default' ? (
                        <span className="muted"> — assigned from LOE alignment</span>
                      ) : detail.lifecycle.loeAlignmentSource === 'rapid' ? (
                        <span className="muted"> — inherited from linked RAPID</span>
                      ) : null}
                    </>
                  ) : (
                    <StatusChip status="blocked">Needs portfolio assignment</StatusChip>
                  )}
                </li>
              ) : null}
            </ul>
            {detail.lifecycle.linkedProjectId ? (
              <Link className="btn ghost sm" to={`/projects/${detail.lifecycle.linkedProjectId}`}>
                View project workspace
              </Link>
            ) : null}
          </article>

          <article className="card">
            <div className="eyebrow">Human review</div>
            <h2 className="card-title">Reviewer position</h2>
            <p className="muted">
              AI baseline score: {detail.scoring.aiBaselineScore ?? 'Not yet run'}
            </p>
            <p className="muted">
              Official reviewed score: {detail.scoring.officialScore ?? 'Not yet set'}
            </p>
            <p className="muted">
              Review disposition: {formatReviewDispositionLabel(detail.scoring.reviewDisposition)}
            </p>
            <p className="muted">
              {request.review?.rationale || 'No human rationale captured yet.'}
            </p>
            <p className="muted">
              Decision:{' '}
              {request.decision?.decision
                ? bucketLabel(request.decision.decision)
                : 'Not yet decided'}
            </p>
          </article>

          <article className="card">
            <div className="eyebrow">Attachments and artifacts</div>
            <h2 className="card-title">Knowledge-linked evidence</h2>
            {detail.artifacts?.length ? (
              <ul className="owha-muted-list">
                {detail.artifacts.map((artifact) => (
                  <li key={artifact.id}>
                    <Link to={artifact.route}>{artifact.title || artifact.id}</Link> ·{' '}
                    {humanizeEnum(artifact.artifactType || 'artifact')}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">No governed artifacts are attached to this intake yet.</p>
            )}
            {detail.knowledgeRoute ? (
              <Link className="btn ghost sm" to={detail.knowledgeRoute}>
                Open knowledge room
              </Link>
            ) : null}
          </article>

          <article className="card">
            <div className="eyebrow">Comments</div>
            <h2 className="card-title">Reviewer and stakeholder thread</h2>
            {detail.comments?.length ? (
              <ul className="owha-muted-list">
                {detail.comments.map((comment) => (
                  <li key={comment.id}>
                    <strong>
                      {comment.authorDisplayName || comment.authorUserId || 'Unknown'}:
                    </strong>{' '}
                    {comment.body}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">No intake comments have been captured yet.</p>
            )}
          </article>

          <article className="card">
            <div className="eyebrow">Decisions</div>
            <h2 className="card-title">Decision history</h2>
            {detail.decisionHistory?.length ? (
              <ul className="owha-muted-list">
                {detail.decisionHistory.map((event) => (
                  <li key={event.id}>
                    <strong>{event.label}</strong> · {event.actorDisplayName || 'Unknown'} ·{' '}
                    {formatAuditTimestamp(event.occurredAt)} ·{' '}
                    {event.rationale || event.outcome || 'No rationale captured'}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">No intake decisions have been recorded yet.</p>
            )}
          </article>

          <article className="card">
            <div className="eyebrow">Audit</div>
            <h2 className="card-title">Audit history</h2>
            {detail.auditHistory?.length ? (
              <ul className="owha-muted-list">
                {detail.auditHistory.map((event) => (
                  <li key={event.id}>
                    <strong title={event.action}>{formatAuditActionLabel(event.action)}</strong> ·{' '}
                    {event.actorDisplayName || 'Unknown'} ·{' '}
                    {event.summary || formatAuditActionLabel(event.action)} ·{' '}
                    {formatAuditTimestamp(event.occurredAt)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">No audit events are available for this intake yet.</p>
            )}
          </article>
        </aside>
      </div>
    </section>
  );
}

function deriveLoeHealthPosture(row) {
  if ((row.overdueActions || 0) > 0 || (row.resourceRollup?.overallocatedCount || 0) > 0) {
    return { status: 'atRisk', label: 'Watch' };
  }
  if ((row.riskSourceSummary?.warningCount || 0) > 0) {
    return { status: 'watch', label: 'Watch' };
  }
  return { status: 'active', label: 'On track' };
}

function loeRowHasWork(row) {
  return (
    ['proposed', 'current', 'completed', 'denied', 'deferred', 'archived'].some(
      (bucket) => Number(row[bucket] || 0) > 0,
    ) ||
    Number(row.openActions || 0) > 0 ||
    Number(row.activeProjects || 0) > 0
  );
}

function collectUnassignedTriageItems(groupedRecords = {}) {
  return LOE_DRILLDOWN_BUCKETS.flatMap((bucket) =>
    (groupedRecords[bucket.key] || []).map((row) => ({
      id: String(row.id || ''),
      entityType: row.entityType === 'project' ? 'project' : 'intake_request',
      title: row.title || row.id,
      bucketLabel: bucket.label,
    })),
  ).filter((item) => item.id);
}

export function LoeDashboardPage() {
  const currentUser = useCurrentUser();
  const { user, roles } = currentUser;
  // AC-LOE-1.2: filter state lives in the URL so views are shareable/bookmarkable.
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => {
    const next = { ...DEFAULT_LOE_FILTERS };
    for (const key of Object.keys(DEFAULT_LOE_FILTERS)) {
      const value = searchParams.get(key);
      if (value) next[key] = value;
    }
    return next;
  }, [searchParams]);
  const [triageOpen, setTriageOpen] = useState(false);
  const [triageTargetLoeId, setTriageTargetLoeId] = useState('');
  const [triageExcludedIds, setTriageExcludedIds] = useState([]);
  const triageMutation = useMutation();
  const res = useApi('/loes', { query: buildLoeQuery(filters) });
  const portfoliosRes = useApi('/portfolios', { query: { limit: 100 } });
  const organizationsRes = useApi('/organizations', { query: { limit: 100 } });
  const resourcesRes = useApi('/resources', { query: { limit: 200 } });
  const unassignedDetailRes = useApi('/loes/Unassigned', { skip: !triageOpen });
  const portfolioRows = portfoliosRes.data?.data || [];
  const organizationRows = organizationsRes.data?.data || [];
  const resourceRows = resourcesRes.data?.data || [];
  const viewerScope = res.data?.meta?.viewerScope || null;
  const divisionScopedViewer = deriveDivisionScopedViewer({
    user,
    roles,
    organizations: organizationRows,
  });
  const relevantDivisionIds = new Set(
    portfolioRows
      .map((portfolio) => portfolio.owningOrganizationId || portfolio.owning_org_id)
      .filter(Boolean),
  );
  const divisionOptions = organizationRows
    .filter(
      (organization) => relevantDivisionIds.size === 0 || relevantDivisionIds.has(organization.id),
    )
    .map((organization) => ({
      value: organization.id,
      label: organization.shortName || organization.name,
    }));
  const portfolioOptions = portfolioRows.map((portfolio) => ({
    value: portfolio.id,
    label: portfolio.shortName || portfolio.name || portfolio.id,
  }));
  const fiscalYearOptions = [
    ...new Set(portfolioRows.map((portfolio) => portfolio.fiscalYear).filter(Boolean)),
  ]
    .sort()
    .map((fiscalYear) => ({ value: fiscalYear, label: fiscalYear }));
  const resourceTeamOptions = [
    ...new Map(
      resourceRows
        .filter((resource) => resource.teamId)
        .map((resource) => [
          resource.teamId,
          {
            value: resource.teamId,
            label: resource.teamName || resource.teamId,
          },
        ]),
    ).values(),
  ].sort((left, right) => left.label.localeCompare(right.label));
  const scopeLockedDivisionId = divisionScopedViewer?.divisionId || viewerScope?.divisionId || '';
  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'divisionId' && value && value === scopeLockedDivisionId) return false;
    return Boolean(value);
  });

  usePageMetadata({
    title: 'Director LOE Portfolio Dashboard',
    subtitle:
      'Director Line of Effort rollups across proposed, current, completed, denied, and deferred work.',
  });

  useEffect(() => {
    if (!scopeLockedDivisionId) return;
    if (searchParams.get('divisionId') === scopeLockedDivisionId) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('divisionId', scopeLockedDivisionId);
        return next;
      },
      { replace: true },
    );
  }, [scopeLockedDivisionId, searchParams, setSearchParams]);

  function updateFilter(field, value) {
    if (field === 'divisionId' && scopeLockedDivisionId) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(field, value);
        else next.delete(field);
        return next;
      },
      { replace: true },
    );
  }

  function clearFilters() {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const key of Object.keys(DEFAULT_LOE_FILTERS)) next.delete(key);
        if (scopeLockedDivisionId) next.set('divisionId', scopeLockedDivisionId);
        return next;
      },
      { replace: true },
    );
  }

  // AC-STATE-1 / AC-LOE-1 (E5-3): lookup fetches degrade to zone banners — only the
  // primary LOE fetch can take the page over.
  const lookupZones = [
    {
      key: 'portfolio',
      label: 'Portfolio lookup failed',
      impact: 'Portfolio and fiscal-year filters are unavailable until it recovers.',
      res: portfoliosRes,
    },
    {
      key: 'organization',
      label: 'Organization lookup failed',
      impact: 'Division filters and division names are unavailable until it recovers.',
      res: organizationsRes,
    },
    {
      key: 'resource',
      label: 'Resource lookup failed',
      impact: 'Resource-team filters are unavailable until it recovers.',
      res: resourcesRes,
    },
  ];
  const lookupsLoading = portfoliosRes.loading || organizationsRes.loading || resourcesRes.loading;

  if (res.loading || lookupsLoading) return <LoadingSkeleton rows={5} />;
  if (res.error) return <ErrorState error={res.error} onRetry={res.refresh} />;

  const allRows = res.data?.data || [];
  // S5 (P4 Cap 3): the enterprise rollup band reads the aggregate the API computed
  // over the full LOE array (all pages + Unassigned). Absent → band simply hidden.
  const enterpriseSummary = res.data?.meta?.enterpriseSummary || null;
  const loeRows = allRows.filter((row) => row.loe !== 'Unassigned');
  const unassignedRow =
    allRows.find((row) => row.loe === 'Unassigned' && loeRowHasWork(row)) || null;
  const triageItems = collectUnassignedTriageItems(unassignedDetailRes.data?.data?.groupedRecords);
  const excludedTriageIds = new Set(triageExcludedIds);
  const selectedTriageItems = triageItems.filter((item) => !excludedTriageIds.has(item.id));
  const triageTargetOptions = loeRows.map((row) => ({
    value: row.loeId || row.loe,
    label: `${row.loe} · ${row.title || row.loe}`,
  }));
  const triageTargetLabel =
    triageTargetOptions.find((option) => option.value === triageTargetLoeId)?.label || '';

  function toggleTriageItem(itemId) {
    setTriageExcludedIds((current) =>
      current.includes(itemId) ? current.filter((value) => value !== itemId) : [...current, itemId],
    );
  }

  async function confirmTriageAssignment() {
    if (!triageTargetLoeId || selectedTriageItems.length === 0) return;
    try {
      const response = await triageMutation.run(() =>
        api.post('/loes/unassigned/assign', {
          targetLoeId: triageTargetLoeId,
          items: selectedTriageItems.map((item) => ({
            entityType: item.entityType,
            id: item.id,
          })),
        }),
      );
      const payload = response?.data || response;
      toast.success(
        `Assigned ${payload?.assignedCount ?? selectedTriageItems.length} unassigned ${pluralize(
          payload?.assignedCount ?? selectedTriageItems.length,
          'record',
        )} to ${payload?.targetLoe || triageTargetLabel}. The assignment was audited.`,
      );
      setTriageOpen(false);
      setTriageTargetLoeId('');
      setTriageExcludedIds([]);
      await res.refresh();
    } catch {
      toast.error('Unassigned work could not be assigned. No records were changed.');
    }
  }

  if (!allRows.length) {
    return (
      <EmptyState
        title={hasActiveFilters ? 'No LOE data matched those filters' : 'No LOE data available'}
        description={
          hasActiveFilters
            ? 'Clear one or more dashboard filters to return to the full Director LOE view.'
            : undefined
        }
      />
    );
  }

  return (
    <section className="owha-grid" data-testid="owha-loe-dashboard-page">
      <PageHeader
        eyebrow="OWHA Strategy"
        title="Director LOE Portfolio Dashboard"
        subtitle="Track intake, active delivery, and action load against each Director Line of Effort."
        actions={
          <div className="row">
            {/* S6 beat (P4 Cap 4): launch the governed redundancy scan from the board,
                reusing the orchestration hub rather than an inline scanner. Enterprise
                scope so the scan spans every portfolio — the three designed
                consolidation pairs live in different portfolios and would be hidden by
                a single-portfolio default. */}
            <Link className="btn ghost" to="/ai/orchestration?mode=redundancy&scope=enterprise">
              Run redundancy scan
            </Link>
            <Link className="btn ghost" to="/project-factory">
              Open intake queue
            </Link>
          </div>
        }
      />

      {scopeLockedDivisionId ? (
        <article className="card">
          <div className="eyebrow">Division-scoped view</div>
          <h2 className="card-title">
            {divisionScopedViewer?.divisionName || viewerScope?.divisionName}
          </h2>
          <p className="muted">
            {divisionScopedViewer?.actorTitle || viewerScope?.actorTitle || 'Division lead'} access
            is automatically limited to this division&apos;s LOE posture.
          </p>
        </article>
      ) : null}

      {lookupZones
        .filter((zone) => zone.res.error)
        .map((zone) => (
          <div
            className="owha-zone-banner"
            role="alert"
            key={zone.key}
            data-testid={`loe-zone-banner-${zone.key}`}
          >
            <div>
              <strong>{zone.label}.</strong>{' '}
              <span>{zone.impact} The LOE grid below still reflects live data.</span>
            </div>
            <button className="btn ghost sm" type="button" onClick={zone.res.refresh}>
              Retry
            </button>
          </div>
        ))}

      <PageActionStrip
        filters={
          <div className="owha-loe-toolbar" data-testid="owha-loe-filter-toolbar">
            <FormField label="Division" htmlFor="loe-filter-division">
              <Select
                id="loe-filter-division"
                value={filters.divisionId}
                onChange={(value) => updateFilter('divisionId', value)}
                disabled={Boolean(scopeLockedDivisionId)}
                options={[{ value: '', label: 'All divisions' }, ...divisionOptions]}
              />
            </FormField>
            <FormField label="Portfolio" htmlFor="loe-filter-portfolio">
              <Select
                id="loe-filter-portfolio"
                value={filters.portfolioId}
                onChange={(value) => updateFilter('portfolioId', value)}
                options={[{ value: '', label: 'All portfolios' }, ...portfolioOptions]}
              />
            </FormField>
            <FormField label="Fiscal year" htmlFor="loe-filter-fiscal-year">
              <Select
                id="loe-filter-fiscal-year"
                value={filters.fiscalYear}
                onChange={(value) => updateFilter('fiscalYear', value)}
                options={[{ value: '', label: 'All fiscal years' }, ...fiscalYearOptions]}
              />
            </FormField>
            <FormField label="Lifecycle stage" htmlFor="loe-filter-lifecycle-stage">
              <Select
                id="loe-filter-lifecycle-stage"
                value={filters.lifecycleStage}
                onChange={(value) => updateFilter('lifecycleStage', value)}
                options={[{ value: '', label: 'All stages' }, ...LIFECYCLE_STAGE_OPTIONS]}
              />
            </FormField>
            <FormField label="Project status" htmlFor="loe-filter-project-status">
              <Select
                id="loe-filter-project-status"
                value={filters.projectStatus}
                onChange={(value) => updateFilter('projectStatus', value)}
                options={[{ value: '', label: 'All statuses' }, ...PROJECT_STATUS_OPTIONS]}
              />
            </FormField>
            <FormField label="Resource team" htmlFor="loe-filter-resource-team">
              <Select
                id="loe-filter-resource-team"
                value={filters.resourceTeamId}
                onChange={(value) => updateFilter('resourceTeamId', value)}
                options={[{ value: '', label: 'All resource teams' }, ...resourceTeamOptions]}
              />
            </FormField>
            <FormField label="Risk severity" htmlFor="loe-filter-risk-severity">
              <Select
                id="loe-filter-risk-severity"
                value={filters.riskSeverity}
                onChange={(value) => updateFilter('riskSeverity', value)}
                options={[{ value: '', label: 'All severities' }, ...RISK_SEVERITY_OPTIONS]}
              />
            </FormField>
            <button
              type="button"
              className="btn ghost sm"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
            >
              Clear filters
            </button>
          </div>
        }
      />

      {enterpriseSummary ? (
        <article
          className="card owha-enterprise-band"
          data-testid="owha-enterprise-band"
          data-feedback-id="loe-enterprise-band"
          aria-label="Division portfolio summary band"
          data-entity-type="organization"
          data-entity-id={scopeLockedDivisionId || 'enterprise'}
        >
          <div className="owha-review-head">
            <div>
              <div className="eyebrow">Single source of truth</div>
              <h2 className="card-title">Enterprise portfolio at a glance</h2>
            </div>
            {/* S8-insight: static consolidation link — never auto-fires a scan on load.
                Enterprise scope so consolidation candidates across every portfolio are
                surfaced, not just the active portfolio's. */}
            <Link
              className="btn ghost sm"
              to="/ai/orchestration?mode=redundancy&scope=enterprise"
              data-testid="owha-enterprise-consolidation-link"
            >
              Review consolidation candidates
            </Link>
          </div>
          <div className="owha-summary-grid">
            <MetricCard
              label="Portfolio records"
              value={enterpriseSummary.totalRecords}
              footer="All states across every LOE"
            />
            <MetricCard
              label="Active projects"
              value={enterpriseSummary.activeProjects}
              footer={`${enterpriseSummary.coverage?.alignedProjects ?? 0} of ${
                enterpriseSummary.coverage?.totalProjects ?? 0
              } active projects aligned to an LOE`}
            />
            <MetricCard
              label="Proposed"
              value={enterpriseSummary.byState?.proposed ?? 0}
              footer="Awaiting decision"
            />
            <MetricCard
              label="Current"
              value={enterpriseSummary.byState?.current ?? 0}
              footer="In delivery"
            />
            <MetricCard
              label="Completed"
              value={enterpriseSummary.byState?.completed ?? 0}
              footer="Closed out"
            />
            <MetricCard
              label="Denied / Deferred"
              value={`${enterpriseSummary.byState?.denied ?? 0} / ${
                enterpriseSummary.byState?.deferred ?? 0
              }`}
              footer="Governed outcomes"
            />
            <MetricCard
              label="Obligated budget"
              value={`$${Number(enterpriseSummary.budget?.obligated ?? 0).toFixed(1)}M`}
              footer={`of $${Number(enterpriseSummary.budget?.planned ?? 0).toFixed(1)}M planned`}
            />
            <MetricCard
              label="Avg utilization"
              value={`${enterpriseSummary.utilization?.averagePercent ?? 0}%`}
              footer={`${enterpriseSummary.utilization?.overAllocatedResourceCount ?? 0} over-allocated`}
            />
          </div>
          <p className="muted small">
            Aggregated across {enterpriseSummary.coverage?.loeCount ?? 0} Lines of Effort, including
            unassigned work.
          </p>
        </article>
      ) : null}

      <div className="card owha-loe-board-card">
        <div className="owha-loe-board-scroll">
          <table className="owha-loe-board" data-testid="owha-loe-board">
            <thead>
              <tr>
                <th scope="col">Line of Effort</th>
                <th scope="col">Health</th>
                <th scope="col" className="owha-loe-board-num">
                  Proposed
                </th>
                <th scope="col" className="owha-loe-board-num">
                  Current
                </th>
                <th scope="col" className="owha-loe-board-num">
                  Completed
                </th>
                <th scope="col" className="owha-loe-board-num">
                  Denied / Deferred
                </th>
                <th scope="col">Actions (open · overdue)</th>
                <th scope="col">Resource load</th>
                <th scope="col">Budget</th>
                <th scope="col">Source trust</th>
              </tr>
            </thead>
            <tbody>
              {loeRows.map((loe) => {
                const health = deriveLoeHealthPosture(loe);
                const budget = loe.budgetSummary || null;
                const riskSourceSummary = loe.riskSourceSummary || null;
                return (
                  <tr
                    key={loe.loe}
                    data-testid={`owha-loe-board-row-${loe.loe}`}
                    data-feedback-id={`loe-board-row-${loe.loe}`}
                    aria-label={`Line of Effort ${loe.loe} — ${loe.title || loe.loe}`}
                    data-entity-type="loe"
                    data-entity-id={loe.loe}
                  >
                    <td>
                      <Link
                        className="owha-loe-board-name"
                        to={buildLoeWorkspacePath(loe.loe, 'prioritize')}
                      >
                        {loe.loe}
                      </Link>
                      <div className="muted small">{loe.title || loe.loe}</div>
                      <div className="muted small">
                        Lead {loe.responsibleLead?.name || 'Unassigned'} ·{' '}
                        {loe.priorityLabel || 'Provisional LOE'}
                      </div>
                    </td>
                    <td>
                      <Link
                        to={buildLoeWorkspacePath(loe.loe, 'delivery')}
                        aria-label={`${loe.loe} health ${health.label}`}
                      >
                        <StatusChip status={health.status}>{health.label}</StatusChip>
                      </Link>
                    </td>
                    <td className="owha-loe-board-num">
                      <Link
                        to={buildLoeWorkspacePath(loe.loe, 'prioritize')}
                        aria-label={`${loe.loe} proposed ${loe.proposed || 0}`}
                      >
                        {loe.proposed || 0}
                      </Link>
                    </td>
                    <td className="owha-loe-board-num">
                      <Link
                        to={buildLoeBucketFilterPath(loe.loe, 'current')}
                        aria-label={`${loe.loe} current ${loe.current || 0}`}
                      >
                        {loe.current || 0}
                      </Link>
                    </td>
                    <td className="owha-loe-board-num">
                      <Link
                        to={buildLoeBucketFilterPath(loe.loe, 'completed')}
                        aria-label={`${loe.loe} completed ${loe.completed || 0}`}
                      >
                        {loe.completed || 0}
                      </Link>
                    </td>
                    <td className="owha-loe-board-num">
                      <Link
                        to={buildLoeBucketFilterPath(loe.loe, 'denied')}
                        aria-label={`${loe.loe} denied ${loe.denied || 0}`}
                      >
                        {loe.denied || 0}
                      </Link>{' '}
                      /{' '}
                      <Link
                        to={buildLoeBucketFilterPath(loe.loe, 'deferred')}
                        aria-label={`${loe.loe} deferred ${loe.deferred || 0}`}
                      >
                        {loe.deferred || 0}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={buildLoeWorkspacePath(loe.loe, 'delivery')}
                        aria-label={`${loe.loe} actions ${loe.openActions || 0} open ${
                          loe.overdueActions || 0
                        } overdue`}
                      >
                        {loe.openActions || 0} open ·{' '}
                        <span
                          className={
                            (loe.overdueActions || 0) > 0 ? 'owha-loe-board-overdue' : undefined
                          }
                        >
                          {loe.overdueActions || 0} overdue
                        </span>
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={buildLoeWorkspacePath(loe.loe, 'resources')}
                        aria-label={`${loe.loe} resource load ${
                          loe.resourceRollup?.utilizationPercent || 0
                        } percent`}
                      >
                        {formatUtilizationLabel(loe.resourceRollup?.utilizationPercent)} ·{' '}
                        {loe.resourceRollup?.overallocatedCount || 0} over-allocated
                      </Link>
                    </td>
                    <td>
                      {budget ? (
                        <div className="owha-loe-budget-cell">
                          <Link
                            to={buildLoeWorkspacePath(loe.loe, 'resources')}
                            aria-label={`${loe.loe} budget`}
                          >
                            <span className="muted small">Obligated </span>$
                            {Number(budget.obligated ?? budget.actual ?? 0).toFixed(1)}M{' '}
                            <span className="muted small">
                              / ${Number(budget.planned ?? 0).toFixed(1)}M planned
                            </span>
                          </Link>
                          {/* S7 (P4 Cap 5) + BL-03: signed obligated delta vs the last
                              committed ingest; an explicit no-baseline state replaces
                              silent omission so testers know why no delta shows. */}
                          {budget.obligatedDeltaUsdM != null ? (
                            <span
                              className={`owha-loe-budget-delta ${
                                Number(budget.obligatedDeltaUsdM) >= 0 ? 'up' : 'down'
                              }`}
                              data-testid={`owha-loe-budget-delta-${loe.loe}`}
                              title="Change in obligated funds vs the last committed ingest baseline"
                            >
                              {Number(budget.obligatedDeltaUsdM) >= 0 ? '+' : '-'}$
                              {Math.abs(Number(budget.obligatedDeltaUsdM)).toFixed(1)}M
                            </span>
                          ) : (
                            <span
                              className="owha-loe-budget-delta none"
                              data-testid={`owha-loe-budget-delta-${loe.loe}`}
                              title="No committed ingest baseline yet — the change chip appears after the next governed financial ingest"
                            >
                              no baseline yet
                            </span>
                          )}
                          {/* S7 freshness sub-line — cleanly absent when no stamp exists. */}
                          {budget.lastUpdatedAt ? (
                            <div className="muted small owha-loe-budget-freshness">
                              Updated {formatTrustUpdatedAt(budget.lastUpdatedAt)}
                              {/* Prefer the readable source label so leadership never
                                  sees a raw connector id (e.g. CONN-PMRT-FINANCE). */}
                              {budget.latestSourceSystemLabel ||
                              budget.latestSourceSystem ||
                              budget.sourceSystem
                                ? ` via ${
                                    budget.latestSourceSystemLabel ||
                                    sourceSystemLabel(
                                      budget.latestSourceSystem || budget.sourceSystem,
                                    )
                                  }`
                                : ''}
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      {riskSourceSummary ? (
                        <Link
                          to={buildLoeWorkspacePath(loe.loe, 'evidence')}
                          aria-label={`${loe.loe} source trust`}
                          title={
                            riskSourceSummary.warningCount
                              ? riskSourceSummary.warnings?.[0] ||
                                'Review source trust before export.'
                              : `Updated ${formatTrustUpdatedAt(riskSourceSummary.lastUpdatedAt)}`
                          }
                        >
                          <DataSourceBadge
                            source={riskSourceSummary.sourceLabel}
                            freshness={riskSourceSummary.freshness}
                            confidence={riskSourceSummary.confidence}
                          />
                        </Link>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {unassignedRow ? (
                <tr
                  className="owha-loe-board-unassigned"
                  data-testid="owha-loe-board-row-unassigned"
                >
                  <td>
                    <strong>Unassigned work</strong>
                    <div className="owha-loe-board-unassigned-note">
                      Not yet aligned to a Line of Effort
                    </div>
                  </td>
                  <td>
                    <StatusChip status="watch">Data hygiene</StatusChip>
                  </td>
                  <td className="owha-loe-board-num">{unassignedRow.proposed || 0}</td>
                  <td className="owha-loe-board-num">{unassignedRow.current || 0}</td>
                  <td className="owha-loe-board-num">{unassignedRow.completed || 0}</td>
                  <td className="owha-loe-board-num">
                    {unassignedRow.denied || 0} / {unassignedRow.deferred || 0}
                  </td>
                  <td>{unassignedRow.openActions || 0} open</td>
                  <td>
                    {formatUtilizationLabel(unassignedRow.resourceRollup?.utilizationPercent)}
                  </td>
                  <td>
                    <span className="muted">—</span>
                  </td>
                  <td>
                    <button
                      className="btn primary sm"
                      type="button"
                      onClick={() => setTriageOpen((current) => !current)}
                    >
                      Triage unassigned
                    </button>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <p className="muted small">
          Every cell links to the matching tab of the LOE Workspace.
          {unassignedRow
            ? ' The Unassigned row appears only while unassigned work exists — triaging it assigns records to a Line of Effort with an audited bulk action.'
            : ''}
        </p>
      </div>

      {triageOpen && unassignedRow ? (
        <article className="card owha-triage-panel" data-testid="owha-loe-triage-panel">
          <div className="owha-review-head">
            <div>
              <div className="eyebrow">Data hygiene</div>
              <h2 className="card-title">Triage unassigned work</h2>
            </div>
            <button className="btn ghost sm" type="button" onClick={() => setTriageOpen(false)}>
              Close
            </button>
          </div>
          <p className="muted">
            Assign the records below to a Line of Effort. The bulk assignment is recorded in the
            audit log with your name.
          </p>
          {unassignedDetailRes.loading ? <LoadingSkeleton rows={3} /> : null}
          {unassignedDetailRes.error ? (
            <ErrorState error={unassignedDetailRes.error} onRetry={unassignedDetailRes.refresh} />
          ) : null}
          {!unassignedDetailRes.loading && !unassignedDetailRes.error ? (
            triageItems.length ? (
              <>
                <ul className="owha-muted-list owha-triage-list">
                  {triageItems.map((item) => (
                    <li key={item.id}>
                      <label className="owha-triage-item">
                        <input
                          type="checkbox"
                          checked={!excludedTriageIds.has(item.id)}
                          onChange={() => toggleTriageItem(item.id)}
                        />
                        <span>
                          <strong>{item.title}</strong> · {item.id} ·{' '}
                          {item.entityType === 'project' ? 'Project' : 'Intake request'} ·{' '}
                          {item.bucketLabel}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
                <div className="owha-triage-controls">
                  <FormField label="Assign to Line of Effort" htmlFor="owha-triage-target">
                    <Select
                      id="owha-triage-target"
                      value={triageTargetLoeId}
                      onChange={setTriageTargetLoeId}
                      placeholder="Select a Line of Effort"
                      options={triageTargetOptions}
                    />
                  </FormField>
                  <button
                    className="btn primary sm"
                    type="button"
                    disabled={
                      triageMutation.running ||
                      !triageTargetLoeId ||
                      selectedTriageItems.length === 0
                    }
                    onClick={confirmTriageAssignment}
                  >
                    {triageMutation.running
                      ? 'Assigning...'
                      : `Confirm: assign ${selectedTriageItems.length} ${pluralize(
                          selectedTriageItems.length,
                          'record',
                        )}${triageTargetLabel ? ` to ${triageTargetLabel}` : ''}`}
                  </button>
                </div>
                {triageMutation.error ? (
                  <p className="form-error" role="alert">
                    {triageMutation.error.message}
                  </p>
                ) : null}
              </>
            ) : (
              <p className="muted">No unassigned records were returned for triage.</p>
            )
          ) : null}
        </article>
      ) : null}
    </section>
  );
}

export function LoeDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const decodedId = decodeURIComponent(id || '');
  const res = useApi(`/loes/${decodedId}`, { skip: !decodedId });
  const briefMutation = useMutation();
  const decisionMutation = useMutation();
  const [detailState, setDetailState] = useState(null);
  const [draftAiRun, setDraftAiRun] = useState(null);
  const [prioritySort, setPrioritySort] = useState('ai_score');
  const [compareIds, setCompareIds] = useState([]);
  const [pendingPriorityDecision, setPendingPriorityDecision] = useState(null);
  const [priorityRationale, setPriorityRationale] = useState('');
  const [busyPriorityDecisionKey, setBusyPriorityDecisionKey] = useState(null);

  usePageMetadata({
    title: decodedId || 'LOE Detail',
    subtitle: 'Director-level posture, top risks, and overdue action queue for the selected LOE.',
  });

  useEffect(() => {
    const nextDetail = res.data?.data || null;
    setDetailState(nextDetail);
    setDraftAiRun(null);
    setPrioritySort('ai_score');
    setCompareIds([]);
  }, [res.data]);

  const detail = detailState || res.data?.data || null;

  if (res.loading && !detail) return <LoadingSkeleton rows={6} />;
  if (res.error?.status === 404) return <EmptyState title="LOE not found" />;
  if (res.error) return <ErrorState error={res.error} onRetry={res.refresh} />;
  if (!detail) return <EmptyState title="LOE not found" />;

  const aiSummary = buildDisplayedAiSummary(detail.aiSummary, draftAiRun);
  const latestAiRun = aiSummary?.latestRun || null;
  const viewerScope = res.data?.meta?.viewerScope || null;
  const groupedRecords = detail.groupedRecords || { counts: {} };
  const metadata = detail.metadata || null;
  const riskHeatmap = detail.riskHeatmap || null;
  const riskSourceSummary = detail.riskSourceSummary || null;
  const contextualDrilldown = detail.contextualDrilldown || null;
  const trendLine = detail.trendLine || null;
  // AC-LOE-2.1 / E6-1: the workspace is tabbed and every tab is URL-addressable.
  const activeTab = normalizeLoeWorkspaceTab(searchParams.get('tab'));
  const selectedBucket = normalizeLoeBucketFilter(searchParams.get('bucket'));
  const visibleDrilldownBuckets = selectedBucket
    ? LOE_DRILLDOWN_BUCKETS.filter((bucket) => bucket.key === selectedBucket)
    : LOE_DRILLDOWN_BUCKETS;
  const currentWorkspaceTarget = buildWorkspaceReturnTarget(location);
  const currentWorkspaceLabel = detail.summary.loe || 'LOE detail';
  const proposedCandidates = sortPriorityCandidates(groupedRecords.proposed || [], prioritySort);
  const comparedCandidates = compareIds
    .map((candidateId) => proposedCandidates.find((row) => String(row.id || '') === candidateId))
    .filter(Boolean);

  async function draftLoeBrief() {
    const response = await briefMutation.run(() =>
      api.post(`/ai/loes/${encodeURIComponent(detail.summary.loe)}/brief`, {}),
    );
    const payload = response?.data || response;
    setDraftAiRun(payload);
    setDetailState((current) => {
      if (!current) return current;
      return {
        ...current,
        aiSummary: buildDisplayedAiSummary(current.aiSummary, payload),
      };
    });
  }

  function toggleComparedCandidate(candidateId) {
    setCompareIds((current) => {
      if (current.includes(candidateId)) {
        return current.filter((value) => value !== candidateId);
      }
      if (current.length >= 2) {
        return [...current.slice(1), candidateId];
      }
      return [...current, candidateId];
    });
  }

  function selectWorkspaceTab(tabKey) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('tab', tabKey);
        return next;
      },
      { replace: false },
    );
  }

  function openPrioritizationConfirm(row, decision) {
    setPriorityRationale('');
    setPendingPriorityDecision({ row, decision });
  }

  // AC-LOE-2.2 / AC-AUD-2 (E6-3): inline approve/defer require an explicitly typed
  // rationale — the previous canned "…from LOE prioritization workspace" strings are gone.
  async function confirmPrioritizationDecision() {
    if (!pendingPriorityDecision) return;
    const { row, decision } = pendingPriorityDecision;
    const rationale = priorityRationale.trim();
    if (!rationale) return;
    const busyKey = `${String(row.id || '')}:${decision}`;
    setBusyPriorityDecisionKey(busyKey);
    try {
      const response = await decisionMutation.run(() =>
        api.post(`/intake/${encodeURIComponent(row.id)}/decision`, {
          decision,
          rationale,
        }),
      );
      const payload = response?.data || response;
      const request = payload?.request || {};
      const nextBucket = decision === 'approved' ? 'current' : 'deferred';

      setDetailState((current) => {
        if (!current) return current;
        const nextGroupedRecords = moveGroupedRecordToBucket(
          current.groupedRecords,
          String(row.id || ''),
          nextBucket,
          {
            ...row,
            bucket: nextBucket,
            statusLabel: request.routingOutcome || row.statusLabel,
            workflowState: request.workflowState || row.workflowState,
          },
        );
        return {
          ...current,
          summary: {
            ...current.summary,
            current: nextGroupedRecords.counts.current,
          },
          groupedRecords: nextGroupedRecords,
        };
      });
      setCompareIds((current) => current.filter((value) => value !== String(row.id || '')));
      setPendingPriorityDecision(null);
      toast.success(
        `${decision === 'approved' ? 'Approved' : 'Deferred'} "${
          row.title || row.id
        }" with your rationale. The decision landed on the intake record.`,
      );
    } catch {
      // decisionMutation.error renders inside the dialog; keep it open for correction.
      toast.error('The prioritization decision could not be recorded.');
    } finally {
      setBusyPriorityDecisionKey(null);
    }
  }

  return (
    <section className="owha-grid" data-testid="owha-loe-detail-page">
      <PageHeader
        eyebrow="Director LOE"
        title={detail.summary.loe}
        subtitle="Overdue actions, top risks, and aligned project posture grounded in current PRIME records."
        actions={
          <div className="row">
            <Link className="btn ghost" to="/analytics/loes">
              Back to LOE dashboard
            </Link>
            <Link
              className="btn ghost"
              to={buildExecutiveDeckPath({ scopeType: 'loe', scopeId: detail.summary.loe })}
            >
              Draft executive deck
            </Link>
            <Link
              className="btn ghost"
              to={buildRedundancyReviewPath({
                scopeType: 'loe',
                scopeId: detail.summary.loe,
                returnTo: currentWorkspaceTarget,
                returnLabel: currentWorkspaceLabel,
              })}
            >
              Run redundancy review
            </Link>
            <Link className="btn primary" to="/actions">
              Open Action Center
            </Link>
          </div>
        }
      />

      {viewerScope?.mode === 'division' ? (
        <article className="card">
          <div className="eyebrow">Division-scoped view</div>
          <h2 className="card-title">{viewerScope.divisionName || 'Scoped division posture'}</h2>
          <p className="muted">
            This LOE detail is limited to the records visible to
            {` ${viewerScope.actorTitle || 'the current division role'}`}.
          </p>
        </article>
      ) : null}

      <div className="owha-summary-grid">
        <MetricCard
          label="Current"
          value={detail.summary.current}
          footer="Active requests/projects"
        />
        <MetricCard
          label="Open actions"
          value={detail.summary.openActions}
          footer="Tracked action load"
        />
        <MetricCard
          label="Overdue actions"
          value={detail.summary.overdueActions}
          footer="Needs Director attention"
        />
        <MetricCard
          label="Active projects"
          value={detail.summary.activeProjects}
          footer="Aligned projects in motion"
        />
      </div>

      <article className="card">
        <div className="owha-review-head">
          <div>
            <div className="eyebrow">Mission profile</div>
            <h2 className="card-title">{metadata?.title || detail.summary.loe}</h2>
          </div>
          {metadata?.priorityLabel ? (
            <StatusChip status={metadata.dashboardColorToken || 'info'}>
              {metadata.priorityLabel}
            </StatusChip>
          ) : null}
        </div>
        <p className="muted">
          {metadata?.narrative ||
            'Director-level mission alignment narrative will appear when LOE governance metadata is available.'}
        </p>
        <div className="owha-loe-meta-grid">
          <article className="owha-loe-meta-card">
            <div className="eyebrow">Owning org</div>
            <strong>{metadata?.owningOrganization?.name || 'Not assigned'}</strong>
          </article>
          <article className="owha-loe-meta-card">
            <div className="eyebrow">Responsible lead</div>
            <strong>{metadata?.responsibleLead?.name || 'Not assigned'}</strong>
          </article>
          <article className="owha-loe-meta-card">
            <div className="eyebrow">Trend line</div>
            <strong>{formatTrendDirectionLabel(trendLine?.direction)}</strong>
            <p className="muted small">
              Latest health score {trendLine?.latestScore ?? 'n/a'}
              {trendLine?.delta ? ` (${trendLine.delta > 0 ? '+' : ''}${trendLine.delta})` : ''}
            </p>
          </article>
        </div>
        {trendLine?.points?.length ? (
          <div className="owha-trend-grid">
            {trendLine.points.map((point) => (
              <article className="owha-trend-card" key={point.period}>
                <div className="owha-review-head">
                  <strong>{point.period}</strong>
                  <span className="muted small">
                    {point.projectCount || 0} project{point.projectCount === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="owha-trend-score">{point.score}</div>
                <div className="owha-trend-bar" aria-hidden="true">
                  <span
                    style={{
                      width: `${Math.max(8, Math.min(100, Number(point.score || 0)))}%`,
                    }}
                  />
                </div>
              </article>
            ))}
          </div>
        ) : null}
      </article>

      <div className="owha-tab-strip" role="tablist" aria-label="LOE workspace sections">
        {LOE_WORKSPACE_TABS.map((tab) => (
          <button
            key={tab.key}
            id={`loe-tab-${tab.key}`}
            role="tab"
            type="button"
            aria-selected={activeTab === tab.key}
            aria-controls={`loe-tabpanel-${tab.key}`}
            className={`owha-tab${activeTab === tab.key ? ' is-active' : ''}`}
            onClick={() => selectWorkspaceTab(tab.key)}
          >
            {tab.label}
            {tab.key === 'prioritize' && proposedCandidates.length ? (
              <span className="owha-tab-badge">{proposedCandidates.length}</span>
            ) : null}
          </button>
        ))}
      </div>

      {activeTab === 'prioritize' ? (
        <div
          role="tabpanel"
          id="loe-tabpanel-prioritize"
          aria-labelledby="loe-tab-prioritize"
          className="owha-grid"
        >
          <article className="card">
            <div className="owha-review-head">
              <div>
                <div className="eyebrow">Director decision queue</div>
                <h2 className="card-title">Director prioritization workspace</h2>
              </div>
              <div className="owha-priority-toolbar">
                <FormField label="Sort proposed work by" htmlFor="loe-priority-sort">
                  <Select
                    id="loe-priority-sort"
                    options={LOE_PRIORITY_SORT_OPTIONS}
                    value={prioritySort}
                    onChange={setPrioritySort}
                  />
                </FormField>
                <div className="row" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                  <Link
                    className="btn ghost sm"
                    to={buildRedundancyReviewPath({
                      scopeType: 'loe',
                      scopeId: detail.summary.loe,
                      returnTo: currentWorkspaceTarget,
                      returnLabel: currentWorkspaceLabel,
                    })}
                  >
                    View redundancy recommendation
                  </Link>
                  <Link
                    className="btn primary sm"
                    to={buildInformationPaperPath({
                      scopeType: 'loe',
                      scopeId: detail.summary.loe,
                    })}
                  >
                    Draft Information Paper
                  </Link>
                </div>
              </div>
            </div>
            {proposedCandidates.length ? (
              <>
                <div className="owha-priority-grid">
                  {proposedCandidates.map((row) => {
                    const isCompared = compareIds.includes(String(row.id || ''));
                    const rowKey = String(row.id || '');
                    return (
                      <article
                        className={`owha-priority-card${isCompared ? ' is-compared' : ''}`}
                        data-testid={`loe-priority-card-${row.id}`}
                        key={row.id}
                      >
                        <div className="owha-review-head">
                          <div>
                            <strong>{row.title || row.id}</strong>
                            <p className="muted small">
                              {row.problemStatement || 'Awaiting deeper intake narrative.'}
                            </p>
                          </div>
                          <StatusChip status={row.bucket || 'proposed'}>
                            {bucketLabel(row.bucket)}
                          </StatusChip>
                        </div>
                        <div className="owha-priority-metrics">
                          <article className="owha-priority-metric">
                            <span className="eyebrow">AI score</span>
                            <strong>{row.aiScore ?? 'n/a'}</strong>
                          </article>
                          <article className="owha-priority-metric">
                            <span className="eyebrow">Mission impact</span>
                            <strong>{row.missionImpactScore ?? 'n/a'}</strong>
                          </article>
                          <article className="owha-priority-metric">
                            <span className="eyebrow">Priority rank</span>
                            <strong>{row.priorityRank ?? 'n/a'}</strong>
                          </article>
                        </div>
                        <p className="muted small">
                          {row.statusLabel
                            ? formatWorkflowStateLabel(row.statusLabel)
                            : 'Pending review'}
                        </p>
                        <div className="owha-review-actions">
                          <Link
                            className="btn ghost sm"
                            to={appendWorkspaceReturnContext(
                              row.route,
                              currentWorkspaceTarget,
                              currentWorkspaceLabel,
                            )}
                          >
                            Open intake
                          </Link>
                          <button
                            className="btn ghost sm"
                            onClick={() => toggleComparedCandidate(String(row.id || ''))}
                            type="button"
                          >
                            Compare {row.title || row.id}
                          </button>
                          <button
                            className="btn ghost sm"
                            disabled={decisionMutation.running}
                            onClick={() => openPrioritizationConfirm(row, 'approved')}
                            type="button"
                          >
                            {busyPriorityDecisionKey === `${rowKey}:approved`
                              ? 'Approving...'
                              : `Approve ${row.title || row.id}`}
                          </button>
                          <button
                            className="btn ghost sm"
                            disabled={decisionMutation.running}
                            onClick={() => openPrioritizationConfirm(row, 'deferred')}
                            type="button"
                          >
                            {busyPriorityDecisionKey === `${rowKey}:deferred`
                              ? 'Deferring...'
                              : `Defer ${row.title || row.id}`}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>

                {comparedCandidates.length ? (
                  <section className="owha-priority-compare">
                    <div className="owha-review-head">
                      <div>
                        <div className="eyebrow">Decision support</div>
                        <h3>Compare proposed work</h3>
                      </div>
                    </div>
                    <div className="owha-priority-compare-grid">
                      {comparedCandidates.map((row) => (
                        <article className="owha-priority-compare-card" key={row.id}>
                          <div className="owha-review-head">
                            <strong>{row.title || row.id}</strong>
                            <StatusChip status={row.bucket || 'proposed'}>
                              {bucketLabel(row.bucket)}
                            </StatusChip>
                          </div>
                          <div className="owha-priority-metrics">
                            <article className="owha-priority-metric">
                              <span className="eyebrow">AI score</span>
                              <strong>{row.aiScore ?? 'n/a'}</strong>
                            </article>
                            <article className="owha-priority-metric">
                              <span className="eyebrow">Mission impact</span>
                              <strong>{row.missionImpactScore ?? 'n/a'}</strong>
                            </article>
                            <article className="owha-priority-metric">
                              <span className="eyebrow">Priority rank</span>
                              <strong>{row.priorityRank ?? 'n/a'}</strong>
                            </article>
                          </div>
                          <p className="muted small">
                            {row.problemStatement || 'Awaiting deeper intake narrative.'}
                          </p>
                        </article>
                      ))}
                    </div>
                  </section>
                ) : null}
              </>
            ) : (
              <p className="muted">
                No proposed requests remain for prioritization. New candidates will appear here when
                intake records are aligned to this LOE.
              </p>
            )}
          </article>
        </div>
      ) : null}

      {activeTab === 'delivery' ? (
        <div
          role="tabpanel"
          id="loe-tabpanel-delivery"
          aria-labelledby="loe-tab-delivery"
          className="owha-grid"
        >
          {selectedBucket ? (
            <article className="card">
              <div className="eyebrow">Status filter</div>
              <div className="owha-review-head">
                <div>
                  <h2 className="card-title">
                    Showing {formatLoeBucketRollupLabel(selectedBucket)} records
                  </h2>
                  <p className="muted">
                    The LOE drilldown below is filtered to the selected status bucket.
                  </p>
                </div>
                <Link
                  className="btn ghost sm"
                  to={buildLoeWorkspacePath(detail.summary.loe, 'delivery')}
                >
                  Show all status buckets
                </Link>
              </div>
            </article>
          ) : null}

          <div className="owha-review-layout">
            <article className="card">
              <div className="owha-review-head">
                <div>
                  <div className="eyebrow">Overdue action queue</div>
                  <h2 className="card-title">Overdue action queue</h2>
                </div>
              </div>
              {detail.overdueActions?.length ? (
                <ul className="owha-muted-list">
                  {detail.overdueActions.map((action) => (
                    <li key={action.id}>
                      <Link
                        to={appendWorkspaceReturnContext(
                          action.route || `/actions/${encodeURIComponent(action.id)}`,
                          currentWorkspaceTarget,
                          currentWorkspaceLabel,
                        )}
                      >
                        {action.title || action.id}
                      </Link>{' '}
                      ·{' '}
                      {action.projectRoute ? (
                        <Link
                          to={appendWorkspaceReturnContext(
                            action.projectRoute,
                            currentWorkspaceTarget,
                            currentWorkspaceLabel,
                          )}
                        >
                          {action.projectName || action.projectId}
                        </Link>
                      ) : (
                        action.projectName || action.projectId || 'Unassigned project'
                      )}{' '}
                      · {action.ownerDisplayName || action.ownerUserId || 'Unassigned'} · due{' '}
                      {action.dueDate || 'No due date'}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No overdue actions for this LOE.</p>
              )}
            </article>

            <aside className="owha-grid">
              <article className="card">
                <div className="eyebrow">Aligned delivery and intake</div>
                <h2 className="card-title">Project and intake drilldown</h2>
                <div className="owha-drilldown-groups">
                  {visibleDrilldownBuckets.map((bucket) => {
                    const rows = groupedRecords[bucket.key] || [];
                    const count = groupedRecords.counts?.[bucket.key] || 0;
                    return (
                      <section className="owha-drilldown-group" key={bucket.key}>
                        <div className="owha-review-head">
                          <strong>{bucket.label}</strong>
                          <span className="muted small">{count}</span>
                        </div>
                        {rows.length ? (
                          <ul className="owha-muted-list">
                            {rows.map((row) => (
                              <li key={`${bucket.key}-${row.entityType}-${row.id}`}>
                                <Link
                                  to={appendWorkspaceReturnContext(
                                    row.route,
                                    currentWorkspaceTarget,
                                    currentWorkspaceLabel,
                                  )}
                                >
                                  {row.title || row.id}
                                </Link>{' '}
                                ·{' '}
                                {row.entityType === 'project'
                                  ? 'Project'
                                  : formatIntakeRequestTypeLabel(row.requestType)}{' '}
                                ·{' '}
                                {row.statusLabel
                                  ? formatWorkflowStateLabel(row.statusLabel)
                                  : 'Pending review'}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="muted">
                            No {bucket.label.toLowerCase()} records for this LOE.
                          </p>
                        )}
                      </section>
                    );
                  })}
                </div>
              </article>

              <article className="card">
                <div className="eyebrow">Deeper navigation</div>
                <h2 className="card-title">Workspace drilldowns</h2>
                {contextualDrilldown?.summary ? (
                  <>
                    <p className="muted">
                      {contextualDrilldown.summary.portfolioCount || 0} portfolio
                      {pluralize(contextualDrilldown.summary.portfolioCount, '')} ·{' '}
                      {contextualDrilldown.summary.programCount || 0} program
                      {pluralize(contextualDrilldown.summary.programCount, '')} ·{' '}
                      {contextualDrilldown.summary.projectCount || 0} project
                      {pluralize(contextualDrilldown.summary.projectCount, '')} ·{' '}
                      {contextualDrilldown.summary.actionCount || 0} open action
                      {pluralize(contextualDrilldown.summary.actionCount, '')} ·{' '}
                      {contextualDrilldown.summary.artifactCount || 0} artifact
                      {pluralize(contextualDrilldown.summary.artifactCount, '')}
                    </p>
                    <div className="owha-context-grid">
                      <section className="owha-context-card">
                        <div className="owha-review-head">
                          <strong>Portfolio context</strong>
                          <span className="muted small">
                            {contextualDrilldown.portfolios?.length || 0}
                          </span>
                        </div>
                        {contextualDrilldown.portfolios?.length ? (
                          <ul className="owha-muted-list">
                            {contextualDrilldown.portfolios.map((portfolio) => (
                              <li key={portfolio.id}>
                                <Link
                                  to={appendWorkspaceReturnContext(
                                    portfolio.route,
                                    currentWorkspaceTarget,
                                    currentWorkspaceLabel,
                                  )}
                                >
                                  {portfolio.name || portfolio.id}
                                </Link>{' '}
                                · {portfolio.projectCount} aligned project
                                {pluralize(portfolio.projectCount, '')}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="muted">
                            No governed portfolio alignment is available for this LOE yet.
                          </p>
                        )}
                        <div className="owha-review-head">
                          <strong>Program context</strong>
                          <span className="muted small">
                            {contextualDrilldown.programs?.length || 0}
                          </span>
                        </div>
                        {contextualDrilldown.programs?.length ? (
                          <ul className="owha-muted-list">
                            {contextualDrilldown.programs.map((program) => (
                              <li key={program.id}>
                                <Link
                                  to={appendWorkspaceReturnContext(
                                    program.route,
                                    currentWorkspaceTarget,
                                    currentWorkspaceLabel,
                                  )}
                                >
                                  {program.name || program.id}
                                </Link>{' '}
                                · {program.projectCount} aligned project
                                {pluralize(program.projectCount, '')}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="muted">
                            No governed program alignment is available for this LOE yet.
                          </p>
                        )}
                      </section>

                      <section className="owha-context-card">
                        <div className="owha-review-head">
                          <strong>Project workspaces</strong>
                          <span className="muted small">
                            {contextualDrilldown.projects?.length || 0}
                          </span>
                        </div>
                        {contextualDrilldown.projects?.length ? (
                          <div className="owha-project-context-list">
                            {contextualDrilldown.projects.map((project) => (
                              <article className="owha-project-context-card" key={project.id}>
                                <div className="owha-review-head">
                                  <strong>{project.name || project.id}</strong>
                                  <StatusChip status={project.status || 'active'} />
                                </div>
                                <p className="muted small">
                                  {project.portfolioName || 'Unassigned portfolio'} ·{' '}
                                  {project.programName || 'Unassigned program'} ·{' '}
                                  {project.openActionCount || 0} open action
                                  {pluralize(project.openActionCount, '')} ·{' '}
                                  {project.artifactCount || 0} artifact
                                  {pluralize(project.artifactCount, '')}
                                </p>
                                <div className="row" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                                  <Link
                                    className="btn ghost sm"
                                    to={appendWorkspaceReturnContext(
                                      project.route,
                                      currentWorkspaceTarget,
                                      currentWorkspaceLabel,
                                    )}
                                  >
                                    Open project workspace
                                  </Link>
                                  <Link
                                    className="btn ghost sm"
                                    to={appendWorkspaceReturnContext(
                                      project.actionRoute,
                                      currentWorkspaceTarget,
                                      currentWorkspaceLabel,
                                    )}
                                  >
                                    Open action list
                                  </Link>
                                  <Link
                                    className="btn ghost sm"
                                    to={appendWorkspaceReturnContext(
                                      project.knowledgeRoute,
                                      currentWorkspaceTarget,
                                      currentWorkspaceLabel,
                                    )}
                                  >
                                    Open knowledge room
                                  </Link>
                                </div>
                              </article>
                            ))}
                          </div>
                        ) : (
                          <p className="muted">
                            No governed project workspace drilldowns are available for this LOE yet.
                          </p>
                        )}
                      </section>

                      <section className="owha-context-card">
                        <div className="owha-review-head">
                          <strong>Action context</strong>
                          <span className="muted small">
                            {contextualDrilldown.actions?.length || 0}
                          </span>
                        </div>
                        {contextualDrilldown.actions?.length ? (
                          <ul className="owha-muted-list">
                            {contextualDrilldown.actions.map((action) => (
                              <li key={action.id}>
                                <Link to={action.route}>{action.title || action.id}</Link> ·{' '}
                                {action.projectRoute ? (
                                  <Link
                                    to={appendWorkspaceReturnContext(
                                      action.projectRoute,
                                      currentWorkspaceTarget,
                                      currentWorkspaceLabel,
                                    )}
                                  >
                                    {action.projectName || action.projectId}
                                  </Link>
                                ) : (
                                  action.projectName || action.projectId || 'Unassigned project'
                                )}{' '}
                                · due {action.dueDate || 'n/a'}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="muted">
                            No governed action drilldowns are available for this LOE yet.
                          </p>
                        )}

                        <div className="owha-review-head">
                          <strong>Artifact context</strong>
                          <span className="muted small">
                            {contextualDrilldown.artifacts?.length || 0}
                          </span>
                        </div>
                        {contextualDrilldown.artifacts?.length ? (
                          <ul className="owha-muted-list">
                            {contextualDrilldown.artifacts.map((artifact) => (
                              <li key={artifact.id}>
                                <Link to={artifact.route}>{artifact.title || artifact.id}</Link> ·{' '}
                                {humanizeEnum(artifact.artifactType || 'artifact')} ·{' '}
                                {artifact.projectRoute ? (
                                  <Link
                                    to={appendWorkspaceReturnContext(
                                      artifact.projectRoute,
                                      currentWorkspaceTarget,
                                      currentWorkspaceLabel,
                                    )}
                                  >
                                    {artifact.projectName || artifact.projectId}
                                  </Link>
                                ) : (
                                  artifact.projectName || artifact.projectId || 'Unassigned project'
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="muted">
                            No governed artifact drilldowns are available for this LOE yet.
                          </p>
                        )}
                      </section>
                    </div>
                  </>
                ) : (
                  <p className="muted">
                    Workspace drilldowns will appear once aligned portfolio, program, project,
                    action, and artifact records exist for this LOE.
                  </p>
                )}
              </article>

              <article className="card">
                <div className="owha-review-head">
                  <div>
                    <div className="eyebrow">Top risks</div>
                    <h2 className="card-title">Risk posture</h2>
                  </div>
                  {riskSourceSummary ? (
                    <DataSourceBadge
                      source={riskSourceSummary.sourceLabel}
                      freshness={riskSourceSummary.freshness}
                      confidence={riskSourceSummary.confidence}
                    />
                  ) : null}
                </div>
                {riskHeatmap?.cells?.length ? (
                  <div className="owha-risk-heatmap" data-testid="owha-risk-heatmap">
                    {riskHeatmap.cells.map((cell) => (
                      <article
                        className={`owha-risk-heatmap-cell${
                          riskHeatmap.highestConcentrationKey === cell.key ? ' is-primary' : ''
                        }`}
                        key={cell.key}
                      >
                        <div className="eyebrow">{cell.label}</div>
                        <strong>{cell.count}</strong>
                        <p className="muted small">
                          {Array.isArray(cell.risks) && cell.risks[0]?.title
                            ? cell.risks[0].title
                            : 'No aligned risks in this posture bucket.'}
                        </p>
                      </article>
                    ))}
                  </div>
                ) : null}
                {riskSourceSummary ? (
                  <>
                    <p className="muted small">
                      Source freshness {formatFreshnessLabel(riskSourceSummary.freshness)} ·
                      confidence{' '}
                      {riskSourceSummary.confidence != null
                        ? Number(riskSourceSummary.confidence).toFixed(2)
                        : 'n/a'}{' '}
                      · updated {formatTrustUpdatedAt(riskSourceSummary.lastUpdatedAt)}
                    </p>
                    {riskSourceSummary.warningCount ? (
                      <p className="muted small">
                        {riskSourceSummary.warningCount} trust warning
                        {riskSourceSummary.warningCount === 1 ? '' : 's'} ·{' '}
                        {riskSourceSummary.warnings?.[0] || 'Review source trust before export.'}
                      </p>
                    ) : null}
                  </>
                ) : null}
                {detail.topRisks?.length ? (
                  <ul className="owha-muted-list">
                    {detail.topRisks.map((risk) => (
                      <li key={risk.id}>
                        <Link
                          to={appendWorkspaceReturnContext(
                            risk.route || '/governance/risks',
                            currentWorkspaceTarget,
                            currentWorkspaceLabel,
                          )}
                        >
                          {risk.title || risk.id}
                        </Link>{' '}
                        · <StatusChip status={risk.severity} />
                        {risk.projectRoute ? (
                          <>
                            {' '}
                            ·{' '}
                            <Link
                              to={appendWorkspaceReturnContext(
                                risk.projectRoute,
                                currentWorkspaceTarget,
                                currentWorkspaceLabel,
                              )}
                            >
                              {risk.projectName || risk.projectId}
                            </Link>
                          </>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">No open risks are aligned to this LOE.</p>
                )}
              </article>
            </aside>
          </div>
        </div>
      ) : null}

      {activeTab === 'resources' ? (
        <div
          role="tabpanel"
          id="loe-tabpanel-resources"
          aria-labelledby="loe-tab-resources"
          className="owha-grid"
        >
          <article className="card">
            <div className="eyebrow">Resource posture</div>
            <h2 className="card-title">Resource posture</h2>
            <p className="muted">
              {detail.resourceSummary?.activeAllocationCount || 0} active allocations across{' '}
              {detail.resourceSummary?.supportingProjectCount || 0} aligned project
              {detail.resourceSummary?.supportingProjectCount === 1 ? '' : 's'}.
            </p>
            <div className="owha-summary-grid">
              <MetricCard
                label="Active allocations"
                value={detail.resourceSummary?.activeAllocationCount || 0}
                footer="Current staffed allocation records"
              />
              <MetricCard
                label="Allocation load"
                value={`${detail.resourceSummary?.totalAllocationPercent || 0}%`}
                footer="Combined active allocation percent"
              />
              <MetricCard
                label="Weekly hours"
                value={formatHoursPerWeek(detail.resourceSummary?.allocationHoursPerWeek)}
                footer="Derived from active resource allocation records"
              />
              <MetricCard
                label="Utilization"
                value={`${detail.resourceSummary?.utilizationPercent || 0}%`}
                footer="Aggregate assigned-resource utilization"
              />
            </div>
            <div
              className="row"
              style={{ gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}
            >
              <Link
                className="btn ghost sm"
                to={detail.resourceSummary?.schedulerRoute || '/resources/scheduler'}
              >
                Open resource scheduler
              </Link>
              {detail.resourceSummary?.overallocatedCount ? (
                <span className="chip amber sm">
                  {detail.resourceSummary.overallocatedCount} overallocated
                </span>
              ) : (
                <span className="chip outline sm">No overallocated resources</span>
              )}
            </div>
            {detail.resourceSummary?.overallocatedResources?.length ? (
              <ul className="owha-muted-list">
                {detail.resourceSummary.overallocatedResources.map((resource) => (
                  <li key={resource.resourceId}>
                    <strong>{resource.displayName}</strong> · {resource.utilizationPercent || 0}% ·{' '}
                    {resource.varianceHours || 0}h over capacity
                    {resource.conflictDetailRoute ? (
                      <>
                        {' '}
                        · <Link to={resource.conflictDetailRoute}>Open conflict detail</Link>
                      </>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
            {detail.resourceBreakdown?.teams?.length ? (
              <div className="owha-resource-drilldown">
                {detail.resourceBreakdown.teams.map((team) => (
                  <article className="owha-resource-team-card" key={team.teamId}>
                    <div className="owha-review-head">
                      <strong>{team.teamName}</strong>
                      <span className="muted small">{formatHoursPerWeek(team.allocatedHours)}</span>
                    </div>
                    <p className="muted">
                      {formatHoursPerWeek(team.allocatedHours)} across {team.resourceCount} assigned
                      person
                      {team.resourceCount === 1 ? '' : 's'}.
                    </p>
                    <ul className="owha-muted-list">
                      {team.resources.map((resource) => (
                        <li key={resource.resourceId}>
                          <strong>{resource.displayName}</strong> ·{' '}
                          {resource.role || 'Assigned resource'} ·{' '}
                          {formatHoursPerWeek(resource.allocatedHours)} ·{' '}
                          {resource.utilizationPercent || 0}% utilization
                          {resource.status === 'overallocated' ? (
                            <>
                              {' '}
                              · {resource.varianceHours || 0}h over capacity
                              {resource.conflictDetailRoute ? (
                                <>
                                  {' '}
                                  ·{' '}
                                  <Link to={resource.conflictDetailRoute}>
                                    Open conflict detail
                                  </Link>
                                </>
                              ) : null}
                            </>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            ) : null}
          </article>

          <article className="card">
            <div className="owha-review-head">
              <div>
                <div className="eyebrow">Budget posture</div>
                <h2 className="card-title">Budget posture</h2>
              </div>
              {/* AC-W3 / GAP B — governed budget source badge. Same source/freshness/
                  confidence + lineage target as the project Money tab and the
                  allocation-conflict page. Links into lineage only when a real
                  financial-summary record backs the rollup; inert otherwise. */}
              {detail.budgetSummary ? (
                <DataSourceBadge
                  source={detail.budgetSummary.sourceSystem || 'Governed PRIME financial records'}
                  freshness={detail.budgetSummary.sourceFreshness}
                  confidence={detail.budgetSummary.sourceConfidence}
                  lineageTargetId={detail.budgetSummary.representativeFinancialRecordId || null}
                />
              ) : null}
            </div>
            {detail.budgetSummary ? (
              <>
                <p className="muted">
                  Planned ${detail.budgetSummary.planned.toFixed(1)}M · Obligated $
                  {Number(
                    detail.budgetSummary.obligated ?? detail.budgetSummary.actual ?? 0,
                  ).toFixed(1)}
                  M · Expended ${detail.budgetSummary.actual.toFixed(1)}M · Forecast at completion $
                  {detail.budgetSummary.forecast.toFixed(1)}M
                </p>
                <p className="muted small">
                  Variance ${detail.budgetSummary.variance.toFixed(1)}M · Burn rate{' '}
                  {Number(detail.budgetSummary.burnRatePercent || 0)}% · Budget source freshness{' '}
                  {formatFreshnessLabel(
                    // OT-02: riskSourceSummary can legitimately be absent — never crash the
                    // budget block on a missing risk-source summary (AC-LOE-2.3).
                    detail.budgetSummary.sourceFreshness ||
                      riskSourceSummary?.freshness ||
                      'unknown',
                  )}
                </p>
                <div className="owha-summary-grid">
                  <MetricCard
                    label="Variance"
                    value={`$${detail.budgetSummary.variance.toFixed(1)}M`}
                    footer={`${detail.budgetSummary.projectCount} aligned project financial record${
                      detail.budgetSummary.projectCount === 1 ? '' : 's'
                    }`}
                  />
                  <MetricCard
                    label="Burn rate"
                    value={`${Number(detail.budgetSummary.burnRatePercent || 0)}%`}
                    footer="Expended against planned budget posture"
                  />
                  <MetricCard
                    label="Source freshness"
                    value={formatFreshnessLabel(
                      detail.budgetSummary.sourceFreshness ||
                        riskSourceSummary?.freshness ||
                        'unknown',
                    )}
                    footer="Latest governed source-trust posture"
                  />
                </div>
              </>
            ) : (
              <p className="muted">No governed budget records are aligned to this LOE yet.</p>
            )}
          </article>
        </div>
      ) : null}

      {activeTab === 'evidence' ? (
        <div
          role="tabpanel"
          id="loe-tabpanel-evidence"
          aria-labelledby="loe-tab-evidence"
          className="owha-grid"
        >
          <article className="card">
            <div className="owha-review-head">
              <div>
                <div className="eyebrow">AI narrative</div>
                <h2 className="card-title">AI narrative</h2>
              </div>
              <button
                className="btn ghost"
                disabled={briefMutation.running}
                onClick={draftLoeBrief}
                type="button"
              >
                {briefMutation.running ? 'Drafting LOE brief...' : 'Draft LOE brief'}
              </button>
            </div>
            {latestAiRun ? (
              <>
                <div className="row" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                  <StatusChip status={latestAiRun.reviewState}>
                    {formatReviewStateLabel(latestAiRun.reviewState)}
                  </StatusChip>
                  <ConfidenceBadge value={latestAiRun.confidence} />
                </div>
                <p>{latestAiRun.summary}</p>
                <p className="muted">
                  {latestAiRun.citationCount || latestAiRun.citations?.length || 0} citations
                  attached · Guardrail {latestAiRun.guardrailOutcome || 'passed'}
                </p>
                {latestAiRun.citations?.length ? (
                  <div className="owha-citation-strip" data-testid="loe-brief-citations">
                    {latestAiRun.citations.map((citation, index) => (
                      <Link
                        key={`${citation.source || 'citation'}-${index}`}
                        className="chip outline sm owha-citation-chip"
                        to={buildCitationRoute(citation)}
                        title={citation.excerpt || citation.source}
                      >
                        [{index + 1}] {citation.source || 'Cited evidence'}
                      </Link>
                    ))}
                  </div>
                ) : null}
                <p className="muted">
                  {aiSummary?.pendingReviewCount || 0} pending review ·{' '}
                  {aiSummary?.reviewedCount || 0} reviewed run
                  {aiSummary?.reviewedCount === 1 ? '' : 's'}
                </p>
                <p className="muted">
                  {latestAiRun.recommendedNextAction ||
                    'Review citations before using this AI output in an official product.'}
                </p>
              </>
            ) : (
              <p className="muted">
                No LOE AI narrative has been drafted yet. Generate a cited draft for Director review
                when needed.
              </p>
            )}
            {briefMutation.error ? (
              <p className="muted">Could not draft LOE brief: {briefMutation.error.message}</p>
            ) : null}
          </article>
        </div>
      ) : null}

      {pendingPriorityDecision ? (
        <DecisionRationaleDialog
          idPrefix="owha-loe-priority-decision"
          title={`${pendingPriorityDecision.decision === 'approved' ? 'Approve' : 'Defer'} "${
            pendingPriorityDecision.row.title || pendingPriorityDecision.row.id
          }"?`}
          consequence={
            pendingPriorityDecision.decision === 'approved'
              ? `Approves this request into the project factory under ${detail.summary.loe}. Your rationale is recorded on the intake's decision history with your name and timestamp.`
              : `Defers this request for follow-up under ${detail.summary.loe}. Your rationale is recorded on the intake's decision history with your name and timestamp.`
          }
          confirmLabel={
            pendingPriorityDecision.decision === 'approved'
              ? 'Confirm approval'
              : 'Confirm deferral'
          }
          rationale={priorityRationale}
          onRationaleChange={setPriorityRationale}
          onCancel={() => setPendingPriorityDecision(null)}
          onConfirm={confirmPrioritizationDecision}
          busy={Boolean(busyPriorityDecisionKey)}
          error={decisionMutation.error}
        />
      ) : null}
    </section>
  );
}
